// ─── src/pages/RegionCategoryPage.jsx ──────────────────────────────────────────
// Editorial intent page for every Region × Category combination.
// e.g. /uk/london/wedding-venues  /italy/tuscany/photographers
// Data-driven: one template, many region×category combos.
// Phase 1: adopts useDirectoryState + transformListing (shared platform logic).
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { usePrerenderReady } from "../utils/prerenderReady";
import { Helmet } from "react-helmet-async";
import { ThemeCtx, useTheme } from "../theme/ThemeContext";
import { getDarkPalette, getLightPalette, DARK_C } from "../theme/tokens";
import { useAura } from "../stores/auraCore";
import { getLocationSEOSync } from "../services/ai/aiLocationSEO";
import { resolveIdentity } from "../seo/identityEngine.js";

import {
  getRegionBySlug,
  getCountryBySlug,
  getRegionsByCountry,
  COUNTRIES,
  VENDOR_CATEGORIES,
  getRegionCategoryPath,
  getRegionCategoryEditorial,
  getRegionCategorySubtitle,
  geoSlugToVendorCategory,
  getVendorCategoryByGeoSlug,
} from "../data/geo.js";
import { VENUES } from "../data/italyVenues";
import { VENDORS } from "../data/vendors.js";
import { getUserCountryFromIP, sortByCountryPriority } from "../services/geoLocationService";
import { supabaseAnon } from "../lib/supabaseClient";

// ── Phase B: Dynamic filter system ──────────────────────────────────────────
import {
  getFilterConfig,
  buildInitialFilters,
  hasActiveFilters as checkActiveFilters,
  SORT_OPTIONS as FILTER_SORT_OPTIONS,
} from "../filters";
import {
  applyVendorFilters,
  applyVendorSort,
  extractFilterOptions,
} from "../filters";

// ── Phase 1: shared directory state (view mode, map, mobile detection) ────────
import { useDirectoryState } from "../hooks/useDirectoryState";
import { useDesignSettings } from "../hooks/useDesignSettings";
import { transformListings, mergeListings } from "../utils/transformListing";
import { buildCardImgs, buildCardVideoUrl } from "../utils/mediaMappers";
import { getGridStyles, getCardWrapperStyles } from "../config/gridStyles";

import SiteFooter from "../components/sections/SiteFooter";
import DirectoryBrands from "../components/sections/DirectoryBrands";
import EmptyResultState from "../components/sections/EmptyResultState";
import NearMatchSection from "../components/sections/NearMatchSection";
import ImmersiveSearch from "../components/search/ImmersiveSearch";
import LuxuryVenueCard from "../components/cards/LuxuryVenueCard";
import LuxuryVendorCard from "../components/cards/LuxuryVendorCard";
import VenueListItemCard from "../components/cards/VenueListItemCard";
import PlannerCard from "../components/cards/PlannerCard";
import { isPubliclyRoutable } from '../utils/publicRoutability.js';
import MASTERMap        from "../components/maps/MASTERMap";
import { PinSyncBus }  from "../components/maps/PinSyncBus";
import QuickViewModal  from "../components/modals/QuickViewModal";
import AICommandBar from "../components/filters/AICommandBar";
import FilterBar    from "../components/filters/FilterBar";
import InfoStrip        from "../components/sections/InfoStrip";
import HomeNav          from "../components/nav/HomeNav";
import RegionRealWeddings from "../components/sections/RegionRealWeddings";
import MasterCategoryCard from "../components/cards/MasterCategoryCard";
import { track } from "../utils/track";
import { rankListingsByAIIntent } from "../utils/aiRelevanceScorer";
import { fetchAISignals } from "../services/aiSignalService";
import { fetchCategoryContent, buildCategoryKey } from "../services/categoryContentService";
import "../category.css";
import { imgUrl } from '../utils/imgUrl.js';

// ── Build a human-readable Aura summary from immersive refinement fields ─────
function buildImmersiveSummary(ref, categoryLabel, regionName, countryName) {
  const parts = [];
  if (ref.style) parts.push(ref.style);
  parts.push(categoryLabel.toLowerCase());
  const loc = (regionName && regionName !== countryName) ? regionName : countryName;
  if (loc) parts.push(`in ${loc}`);
  const gMap = {
    "Just us":  "for an intimate elopement",
    "Up to 50": "for up to 50 guests",
    "50–100":   "for 50–100 guests",
    "100–200":  "for 100–200 guests",
    "200+":     "for over 200 guests",
  };
  if (ref.guests && gMap[ref.guests]) parts.push(gMap[ref.guests]);
  if (ref.setting && !["Both","Any Season","Standard","Flexible"].includes(ref.setting))
    parts.push(`with an ${ref.setting.toLowerCase()} setting`);
  if (parts.length <= 1) return null;
  const s = parts.join(" ");
  return s.charAt(0).toUpperCase() + s.slice(1) + ".";
}

// ── Generate nearby locations for internal linking clusters ─────────────────
function generateNearbyLocations(countrySlug, regionSlug, categorySlug) {
  // Map of countries → regions → nearby locations
  const nearbyMap = {
    italy: {
      "amalfi-coast": [
        { name: "Ravello", path: "/italy/amalfi-coast/ravello", type: "city" },
        { name: "Positano", path: "/italy/amalfi-coast/positano", type: "city" },
        { name: "Lake Como", path: "/italy/lake-como", type: "region" },
        { name: "Tuscany", path: "/italy/tuscany", type: "region" },
        { name: "Sicily", path: "/italy/sicily", type: "region" },
      ],
      "lake-como": [
        { name: "Bellagio", path: "/italy/lake-como/bellagio", type: "city" },
        { name: "Varenna", path: "/italy/lake-como/varenna", type: "city" },
        { name: "Amalfi Coast", path: "/italy/amalfi-coast", type: "region" },
        { name: "Tuscany", path: "/italy/tuscany", type: "region" },
        { name: "Veneto", path: "/italy/veneto", type: "region" },
      ],
      "tuscany": [
        { name: "Florence", path: "/italy/tuscany/florence", type: "city" },
        { name: "Siena", path: "/italy/tuscany/siena", type: "city" },
        { name: "Amalfi Coast", path: "/italy/amalfi-coast", type: "region" },
        { name: "Lake Como", path: "/italy/lake-como", type: "region" },
        { name: "Sicily", path: "/italy/sicily", type: "region" },
      ],
      "sicily": [
        { name: "Palermo", path: "/italy/sicily/palermo", type: "city" },
        { name: "Taormina", path: "/italy/sicily/taormina", type: "city" },
        { name: "Amalfi Coast", path: "/italy/amalfi-coast", type: "region" },
        { name: "Tuscany", path: "/italy/tuscany", type: "region" },
        { name: "Lake Como", path: "/italy/lake-como", type: "region" },
      ],
      "veneto": [
        { name: "Venice", path: "/italy/veneto/venice", type: "city" },
        { name: "Lake Como", path: "/italy/lake-como", type: "region" },
        { name: "Tuscany", path: "/italy/tuscany", type: "region" },
      ],
    },
    // UK nearby locations
    uk: {
      "london": [
        { name: "Cotswolds", path: "/uk/cotswolds", type: "region" },
        { name: "Oxfordshire", path: "/uk/oxfordshire", type: "region" },
      ],
      "cotswolds": [
        { name: "London", path: "/uk/london", type: "region" },
        { name: "Oxfordshire", path: "/uk/oxfordshire", type: "region" },
      ],
    },
    // France nearby locations
    france: {
      "paris": [
        { name: "Loire Valley", path: "/france/loire-valley", type: "region" },
        { name: "Provence", path: "/france/provence", type: "region" },
      ],
      "provence": [
        { name: "Paris", path: "/france/paris", type: "region" },
        { name: "Loire Valley", path: "/france/loire-valley", type: "region" },
      ],
    },
  };

  // Fallback: return empty array if no mapping exists
  if (!countrySlug || !regionSlug) return [];
  const countryNearby = nearbyMap[countrySlug];
  if (!countryNearby) return [];
  return countryNearby[regionSlug] || [];
}

// ── Font tokens ──────────────────────────────────────────────────────────────
const GD = "var(--font-heading-primary)";
const NU = "var(--font-body)";

// ── Default hero fallback is now set via Admin → Settings → Design ───────────

export default function RegionCategoryPage({
  onBack = () => {},
  onBackHome = () => {},
  onViewVenue = () => {},
  onViewVendor = null,          // vendor public profile nav, falls back to onViewVenue if not set
  onViewCategory = () => {},
  onViewRegion = () => {},
  onViewRegionCategory = () => {},
  onViewCountry = () => {},
  onViewPlanner = () => {},
  countrySlug = null,
  regionSlug = null,
  categorySlug = null,
  citySlug = null,
  footerNav = {},
}) {
  // Resolved handler: vendor clicks go to vendor public profile; fall back to venue handler
  const handleViewVendor = onViewVendor || onViewVenue;
  // ── Design settings (admin-controlled defaults) ───────────────────────────
  const designSettings = useDesignSettings();
  // ── Theme (global, persisted) ─────────────────────────────────────────────
  const themeCtx = useTheme();
  const darkMode = themeCtx.darkMode;

  // ── State ──────────────────────────────────────────────────────────────────
  const [scrolled, setScrolled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [heroSlideIdx, setHeroSlideIdx] = useState(0);
  const [immersiveOpen, setImmersiveOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [seoExpanded, setSeoExpanded] = useState(false);
  const [editorialExpanded, setEditorialExpanded] = useState(false);
  const [savedIds, setSavedIds] = useState([]);
  const [qvItem, setQvItem] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [userCountryCode, setUserCountryCode] = useState(null);
  const [dbListings, setDbListings] = useState([]);
  const [listingsLoaded, setListingsLoaded] = useState(false);
  usePrerenderReady(listingsLoaded);
  const [listingsError, setListingsError] = useState(null);
  // Render-only pagination: filtering/sorting/search/SEO all still operate on
  // the FULL finalListings, only the rendered card count is capped. "Load
  // More" reveals another batch from the already-computed list, no refetch.
  const VENUE_CARDS_PAGE_SIZE = 21;
  const [visibleVenueCount, setVisibleVenueCount] = useState(VENUE_CARDS_PAGE_SIZE);
  // Country + region filter, only used on global routes (e.g. /wedding-venues) where countrySlug is null
  const [localCountryFilter, setLocalCountryFilter] = useState('all');
  const [localRegionFilter,  setLocalRegionFilter]  = useState('all');
  // AI relevance intent, populated when AICommandBar fires onAIIntent, cleared on reset
  const [aiIntent,      setAiIntent]      = useState(null);
  // Behavioral signals, click+enquiry counts per listing, fetched after intent resolves
  const [aiSignals,     setAiSignals]     = useState({});
  // Name match results, populated when user types a brand/venue name, bypasses filters
  const [nameMatches,   setNameMatches]   = useState(null);
  // Entity fallback, fires when named venue isn't in directory, surfaces alternatives
  const [entityFallback, setEntityFallback] = useState(null);

  const [dbHeroSubtitle,      setDbHeroSubtitle]      = useState(null);
  const [auraSummary,         setAuraSummary]         = useState(null);
  const [summaryDismissed,    setSummaryDismissed]    = useState(false);
  const [auraMapFilter,       setAuraMapFilter]       = useState(null);
  const [auraCrossNav,        setAuraCrossNav]        = useState(null);
  const [auraRecommendedIds,  setAuraRecommendedIds]  = useState(null);
  const [sparseZoneAlert,     setSparseZoneAlert]     = useState(null);
  const [isFilteringTransition, setIsFilteringTransition] = useState(false);

  // ── Category flags ──────────────────────────────────────────────────────────
  const isVenue = categorySlug === "wedding-venues";
  // Phase B unified: ALL categories (including venues) go through dynamic filter engine
  const isVendor = true; // Every category uses the dynamic system now

  // Initialize region filter from URL regionSlug (resolve slug → name for display)
  const initialRegionName = useMemo(() => {
    if (!regionSlug) return "All";
    const r = getRegionBySlug(regionSlug);
    return r?.name || "All";
  }, [regionSlug]);

  // ── Phase B: Dynamic vendor filter state (one state for ALL vendor categories) ──
  const vendorFilterConfig = useMemo(() => {
    if (!isVendor) return null;
    return getFilterConfig(categorySlug);
  }, [isVendor, categorySlug]);

  const [vendorFilters, setVendorFilters_raw] = useState(() => {
    if (!vendorFilterConfig) return { sort: "recommended" };
    return buildInitialFilters(vendorFilterConfig, initialRegionName);
  });

  // ── Intelligence: category browse ────────────────────────────────────────
  useEffect(() => {
    if (categorySlug) {
      track('category_browse', {
        category: categorySlug,
        country:  countrySlug  || null,
        region:   regionSlug   || null,
      });
    }
  }, [categorySlug, countrySlug, regionSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-initialize vendor filters when category changes
  useEffect(() => {
    if (vendorFilterConfig) {
      setVendorFilters_raw(buildInitialFilters(vendorFilterConfig, initialRegionName));
    }
  }, [categorySlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync vendor region filter state when URL regionSlug changes (navigation)
  useEffect(() => {
    setVendorFilters_raw(f => ({ ...f, region: initialRegionName }));
  }, [initialRegionName]);

  const setVendorFilters = useCallback((update) => {
    if (typeof update === "function") {
      setVendorFilters_raw(prev => {
        const next = update(prev);
        track('filter_applied', {
          category: categorySlug,
          country:  countrySlug  || null,
          region:   regionSlug   || null,
          filters:  next,
        });
        return next;
      });
    } else {
      track('filter_applied', {
        category: categorySlug,
        country:  countrySlug  || null,
        region:   regionSlug   || null,
        filters:  update,
      });
      setVendorFilters_raw(update);
    }
  }, [categorySlug, countrySlug, regionSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Phase 1: shared directory state (replaces viewMode/mapOn/isMobile/activeListingId) ──
  const {
    filters,
    updateFilters,
    viewMode,
    setViewMode,
    mapOn,
    toggleMap:       handleToggleMap,
    setMapOn,
    mapTransitioning,
    activeListingId,
    setActiveListingId,
    isMobile,
  } = useDirectoryState({
    initialFilters: {
      region:   regionSlug   || null,
      country:  countrySlug  || null,
      category: categorySlug || null,
    },
    storageKey: `lwd_map_${countrySlug || "global"}_${regionSlug || "all"}_${categorySlug || "all"}`,
    defaultMapOn: false,
  });

  // ── Legacy venue filter shim removed, all categories now use vendorFilters ──

  const C = darkMode ? getDarkPalette() : getLightPalette();

  // ── Apply immersive refinement filters on mount ───────────────────────────
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("lwd:immersive-refinement");
      if (!raw) return;
      sessionStorage.removeItem("lwd:immersive-refinement");
      const ref = JSON.parse(raw);
      // ── Canonical mapping layer ───────────────────────────────────────────
      // Single source of truth for immersive → filter value translation.
      // Covers both Step 2 refinement labels and chip shorthand values so
      // nothing is silently dropped regardless of where the value originates.
      const capacityMap = {
        // Step 2 refinement options
        "Just us":   "Up to 50",
        "Up to 50":  "Up to 50",
        "50–100":    "50–100",
        "100–200":   "100–200",
        "200+":      "200+",
        // Chip shorthand values
        "elopement": "Up to 50",
        "small":     "Up to 50",
      };
      const priceMap = { "Mid-range £££": "£££", "Luxury ££££": "££££" };
      setVendorFilters(prev => {
        const next = { ...prev };
        if (ref.style)                               next.style    = ref.style;
        if (ref.setting)                             next.setting  = ref.setting;
        if (ref.guests && capacityMap[ref.guests])   next.capacity = capacityMap[ref.guests];
        if (ref.budget && priceMap[ref.budget])      next.budget   = priceMap[ref.budget];
        return next;
      });
      // Build concierge summary banner text
      const summary = buildImmersiveSummary(ref, categoryLabel, regionName, countryName);
      if (summary) { setAuraSummary(summary); setSummaryDismissed(false); }
    } catch (_) { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── View mode handler (wraps shared setViewMode) ─────────────────────────
  // List view always opens with the map (split layout). Blocked on mobile.
  const handleViewMode = useCallback((mode) => {
    if (mode === "list" && isMobile) return; // list view disabled on mobile
    setViewMode(mode);
    if (mode === "list" && !mapOn) setMapOn(true);
  }, [setViewMode, mapOn, setMapOn, isMobile]);

  // If user arrives on mobile with list view saved in storage, reset to grid
  useEffect(() => {
    if (isMobile && viewMode === "list") setViewMode("grid");
  }, [isMobile, viewMode, setViewMode]);

  // List view on desktop always requires the map, enforce it
  useEffect(() => {
    if (!isMobile && viewMode === "list" && !mapOn) setMapOn(true);
  }, [isMobile, viewMode, mapOn, setMapOn]);

  // ── Fetch published listings directly, no admin edge function ───────────
  // Uses public Supabase query (anon key). Only reads published rows.
  // No admin session required. Safe for all visitors.
  useEffect(() => {
    if (!supabaseAnon) return; // supabase not configured (dev without .env)

    // Reset on every navigation so stale data never shows while loading
    setDbListings([]);
    setListingsLoaded(false);
    setListingsError(null);

    const doFetch = async () => {
      try {
        // Build the query fresh each attempt (Supabase builders are single-use).
        const buildQuery = () => {
          let query = supabaseAnon
            .from('listings')
            .select(
              'id, slug, venue_name, name, card_title, card_summary, card_badge, card_image,' +
              'category, category_slug, listing_type,' +
              'country, country_slug, region, region_slug, city, city_slug,' +
              'short_description, hero_image, hero_images, hero_video_url, media_items, is_featured, status,' +
              'price_from, price_from_numeric, price_range, price_currency, price_label,' +
              'capacity_min, capacity_max,' +
              'is_verified, rating, review_count,' +
              'lwd_score, content_quality_score,' +
              'editorial_approved, editorial_fact_checked, editorial_last_reviewed_at,' +
              'service_tier, luxury_tier,' +
              'lat, lng, styles, specialties, amenities, setting, updated_at,' +
              'bridal_virtual_tryon'
            )
            .eq('status', 'published');

          if (regionSlug)       query = query.eq('region_slug', regionSlug);
          else if (countrySlug) query = query.eq('country_slug', countrySlug);
          else if (localCountryFilter && localCountryFilter !== 'all') {
            query = query.eq('country_slug', localCountryFilter);
            if (localRegionFilter && localRegionFilter !== 'all') query = query.eq('region_slug', localRegionFilter);
          }
          if (citySlug)         query = query.eq('city_slug', citySlug);
          return query;
        };

        // Retry on error OR empty result. Under prerender concurrency (6 workers) — and live
        // burst load — a high-volume hub fetch (100–310 rows) intermittently errored or came
        // back empty against Supabase, and the old catch path still flipped listingsLoaded
        // true, so the hub snapshotted with ZERO cards (media/jsonld/internal-link loss on the
        // site's most important discovery pages). Up to 3 attempts with small backoff; a
        // genuinely-empty category simply exhausts the retries quickly and renders empty.
        const MAX_ATTEMPTS = 5;
        let data = [];
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
          const { data: d, error } = await buildQuery();
          if (error) {
            if (attempt === MAX_ATTEMPTS) throw error;
          } else {
            data = d || [];
            if (data.length > 0) break;
          }
          // Backoff with jitter so concurrent workers that failed together don't retry in
          // lockstep (which would re-collide against the same load).
          if (attempt < MAX_ATTEMPTS) await new Promise((r) => setTimeout(r, 300 * attempt + Math.random() * 250));
        }

        // Map snake_case DB columns to the camelCase shape transformListing expects
        const rows = (data || []).map(r => ({
          id:               r.id,
          slug:             r.slug,
          name:             r.venue_name || r.name || '',
          cardTitle:        r.card_title   || '',
          cardSummary:      r.card_summary || '',
          cardBadge:        r.card_badge   || '',
          shortDescription: r.short_description || '',
          // heroImage: primary source is first image from media_items (Listing Studio).
          // Fallback to card_image/hero_image for legacy listings without media_items.
          heroImage:        (() => {
            // Defensive: clean malformed media_items where url is object instead of string
            const cleanedMediaItems = (Array.isArray(r.media_items) ? r.media_items : []).map(item => {
              if (typeof item.url === 'string') return item;
              if (typeof item.url === 'object' && item.url) {
                // Try to extract URL from object properties
                const extracted = item.url.path || item.url.url || item.url.src || item.url.file || item.url.publicUrl;
                return { ...item, url: extracted };
              }
              return item;
            });
            const mediaImgs = buildCardImgs(cleanedMediaItems);
            if (mediaImgs.length > 0 && mediaImgs[0]?.src) {
              return mediaImgs[0].src;
            }
            return r.card_image || r.hero_image || '';
          })(),
          // imgs: priority 1 = media_items gallery (Listing Studio, rich objects with
          // credits/sort/featured). Priority 2 = hero_images JSONB (older intakes).
          // Empty falls back to heroImage via normaliseImgs in transformListing.
          imgs: (() => {
            // Defensive: clean malformed media_items where url is object instead of string
            const cleanedMediaItems = (Array.isArray(r.media_items) ? r.media_items : []).map(item => {
              if (typeof item.url === 'string') return item;
              if (typeof item.url === 'object' && item.url) {
                const extracted = item.url.path || item.url.url || item.url.src || item.url.file || item.url.publicUrl;
                return { ...item, url: extracted };
              }
              return item;
            });
            const fromMedia = buildCardImgs(cleanedMediaItems)
              .filter(img => img.src);
            if (fromMedia.length > 0) return fromMedia;
            if (Array.isArray(r.hero_images) && r.hero_images.length > 0) {
              return r.hero_images
                .map(h => (typeof h === 'string' ? h : (h?.url || h?.src || '')))
                .filter(Boolean);
            }
            // Fallback: use card_image or hero_image if available
            const cardImage = r.card_image || r.hero_image;
            return cardImage ? [cardImage] : [];
          })(),
          // videoUrl: primary = video item inside media_items; fallback = hero_video_url column
          videoUrl: (() => {
            const fromMedia = buildCardVideoUrl(Array.isArray(r.media_items) ? r.media_items : []);
            return fromMedia || r.hero_video_url || null;
          })(),
          region:           r.region       || '',
          regionSlug:       r.region_slug  || '',
          countrySlug:      r.country_slug || '',
          country:          r.country      || '',
          city:             r.city         || '',
          citySlug:         r.city_slug    || '',
          category:         r.category_slug || r.category || '',
          categorySlug:     r.category_slug || '',
          listingType:      r.listing_type  || '',
          isFeatured:       r.is_featured   ?? false,
          isVerified:       r.is_verified   ?? false,
          status:           r.status,
          priceFrom:        r.price_from    || '',
          priceFromRaw:     r.price_from_numeric ?? null,
          priceRange:       r.price_range   || '',
          priceCurrency:    r.price_currency || '',
          priceLabel:       r.price_label   || '',
          capacity:         r.capacity_max  ?? r.capacity_min ?? null,
          rating:           r.rating        ?? null,
          reviewCount:      r.review_count  ?? null,
          lwdScore:         r.lwd_score     ?? null,
          contentQualityScore:  r.content_quality_score    ?? null,
          editorialApproved:    r.editorial_approved       ?? false,
          editorialFactChecked: r.editorial_fact_checked   ?? false,
          editorialLastReviewedAt: r.editorial_last_reviewed_at ?? null,
          serviceTier:      r.service_tier || r.luxury_tier || '',
          lat:              r.lat           ?? null,
          lng:              r.lng           ?? null,
          styles:           Array.isArray(r.styles)      ? r.styles      : [],
          specialties:      Array.isArray(r.specialties) ? r.specialties : [],
          amenities:        Array.isArray(r.amenities)   ? r.amenities   : [],
          setting:          r.setting       || '',
          bridalVirtualTryon: r.bridal_virtual_tryon ?? false,
        }));

        setDbListings(rows);
        setListingsLoaded(true);
      } catch (e) {
        console.error('[RegionCategoryPage] listings fetch failed:', e);
        setListingsError(e?.message || 'Failed to load listings');
        setListingsLoaded(true); // unblock the render so page still shows
      }
    };

    doFetch();
  }, [regionSlug, countrySlug, categorySlug, citySlug, localCountryFilter, localRegionFilter]);

  // ── Fetch hero subtitle from category_content DB (admin-editable) ─────────
  useEffect(() => {
    if (!categorySlug) return;
    setDbHeroSubtitle(null);
    const key = buildCategoryKey('parent', categorySlug);
    if (!key) return;
    fetchCategoryContent(key).then(data => {
      if (data?.hero_subtitle) setDbHeroSubtitle(data.hero_subtitle);
    }).catch(() => {});
  }, [categorySlug]);

  // ── Register active context with global chat ──────────────────────────────
  const setChatContext = useAura((s) => s.setChatContext);
  useEffect(() => {
    setChatContext({ country: countrySlug, region: regionSlug, category: categorySlug, page: "region-category" });
  }, [setChatContext, countrySlug, regionSlug, categorySlug]);

  // ── Detect user's country for geo-targeted reordering ──────────────────────
  useEffect(() => {
    (async () => {
      const detectedCountry = await getUserCountryFromIP();
      if (detectedCountry) {
        setUserCountryCode(detectedCountry);
      }
    })();
  }, []);

  // ── Data lookups ───────────────────────────────────────────────────────────
  const region = useMemo(() => {
    const r = getRegionBySlug(regionSlug);
    // If getRegionBySlug falls back to the ALL_REGIONS_SENTINEL (slug="all"),
    // treat as unknown region so we still display a clean name from the slug.
    if (!r || r.slug === "all") return null;
    return r;
  }, [regionSlug]);
  const country = useMemo(() => getCountryBySlug(countrySlug), [countrySlug]);
  const vcObj = useMemo(() => getVendorCategoryByGeoSlug(categorySlug), [categorySlug]);
  // Hardcoded H1 labels for the two aggregate pages; all others use the registry label as-is
  const PAGE_LABELS = {
    'wedding-venues':  'Wedding Venue Collective',
    'wedding-vendors': 'The Finest Wedding Creatives',
  };
  const _baseLabel = vcObj?.label || categorySlug || "Category";
  const categoryLabel = PAGE_LABELS[categorySlug] && !countrySlug && !regionSlug
    ? PAGE_LABELS[categorySlug]
    : _baseLabel;
  const categoryIcon = vcObj?.icon || "📋";

  const countryName = country?.name || "";
  const regionName = region?.name ||
    (regionSlug ? regionSlug.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") : countryName);
  const _heroImgSingle = region?.heroImg || "";
  const heroImages = useMemo(() => {
    const raw = (() => {
      if (_heroImgSingle) return [_heroImgSingle];
      const catHero = designSettings.category_heroes?.[categorySlug] || "";
      if (catHero) return [catHero];
      const slider = designSettings.default_hero_slider || [];
      if (slider.length > 0) return slider;
      const def = designSettings.default_hero_image || "";
      return def ? [def] : [];
    })();
    return raw.map((u) => imgUrl(u, { width: 1800 }));
  }, [_heroImgSingle, categorySlug, designSettings]);
  const heroImg = heroImages[heroSlideIdx] || heroImages[0] || "";

  // Auto-rotate hero slider when multiple images
  useEffect(() => {
    if (heroImages.length <= 1) return;
    const t = setInterval(() => setHeroSlideIdx(i => (i + 1) % heroImages.length), 5500);
    return () => clearInterval(t);
  }, [heroImages.length]);

  const editorial = useMemo(
    () => getRegionCategoryEditorial(regionSlug, categorySlug),
    [regionSlug, categorySlug],
  );
  // DB value (from Category Studio) takes priority; geo.js is the fallback
  const heroSubtitle = dbHeroSubtitle || getRegionCategorySubtitle(regionSlug, categorySlug);

  // ── Listings, DB only, no static fallback ───────────────────────────────
  const listings = useMemo(() => {
    if (categorySlug === "wedding-venues") {
      const venueRows = dbListings.filter((l) => {
        const cat = l.categorySlug || l.category || '';
        return cat === 'wedding-venues' || cat === 'venue' || l.listingType === 'venue';
      });
      return transformListings(venueRows, { type: "venue", category: "wedding-venues" });
    }

    if (categorySlug === "wedding-vendors") {
      const rows = dbListings.filter((l) => {
        const cat = l.categorySlug || l.category_slug || l.category || "";
        return cat !== "wedding-venues";
      });
      const dbFiltered = regionSlug
        ? rows.filter((l) => (l.regionSlug || l.region_slug) === regionSlug)
        : countrySlug
        ? rows.filter((l) => (l.countrySlug || l.country_slug) === countrySlug)
        : rows;
      return transformListings(dbFiltered, { type: "vendor" });
    }

    const vendorCats = geoSlugToVendorCategory(categorySlug);
    if (!vendorCats) return [];

    const dbForCategory = dbListings.filter((l) => {
      const lCat = l.categorySlug || l.category || '';
      return lCat === categorySlug || vendorCats.includes(lCat);
    });
    const dbFiltered = regionSlug
      ? dbForCategory.filter((l) => (l.regionSlug || '') === regionSlug)
      : countrySlug
      ? dbForCategory.filter((l) => (l.countrySlug || '') === countrySlug)
      : dbForCategory;
    return transformListings(dbFiltered, { type: "vendor" });
  }, [categorySlug, regionSlug, countrySlug, region, regionName, dbListings]);

  // ── Unified filtering + sorting (one engine for ALL categories) ─────────────
  const finalListings = useMemo(() => {
    if (!vendorFilterConfig) return listings;
    const filtered = applyVendorFilters(listings, vendorFilters, vendorFilterConfig.filters);
    const sorted   = applyVendorSort(filtered, vendorFilters.sort || "recommended");
    // AI intent active: rank by relevance, skip geo-priority (would undo the ranking)
    if (aiIntent) return rankListingsByAIIntent(sorted, aiIntent, aiSignals);
    if (!userCountryCode) return sorted;
    return sortByCountryPriority(sorted, userCountryCode, "countrySlug");
  }, [vendorFilterConfig, listings, vendorFilters, userCountryCode, aiIntent, aiSignals]);
  const listingCount = finalListings.length;

  // Reset the visible-card batch whenever the underlying filtered set changes
  // (new filter, search, region/category nav) so "Load More" always starts
  // fresh at VENUE_CARDS_PAGE_SIZE rather than carrying over a stale count.
  useEffect(() => {
    setVisibleVenueCount(VENUE_CARDS_PAGE_SIZE);
  }, [finalListings]);

  // Rendered-card slice only, filtering, sorting, search, the map, schema.org
  // counts, and SEO content all still see the full finalListings.
  const visibleVenueListings = useMemo(
    () => finalListings.slice(0, visibleVenueCount),
    [finalListings, visibleVenueCount],
  );
  const hasMoreVenueCards = visibleVenueCount < finalListings.length;

  // ── Phase B: Auto-populate filter options from data ─────────────────────────
  const allCountryRegions = useMemo(() => {
    const slug = countrySlug || (localCountryFilter !== 'all' ? localCountryFilter : null);
    if (!slug) return [];
    return getRegionsByCountry(slug).map(r => r.name).sort();
  }, [countrySlug, localCountryFilter]);

  // Full region objects for the simple venue filter bar dropdowns
  const localRegionOptions = useMemo(() => {
    if (!localCountryFilter || localCountryFilter === 'all') return [];
    return getRegionsByCountry(localCountryFilter).sort((a, b) => a.name.localeCompare(b.name));
  }, [localCountryFilter]);

  // Region options passed to FilterBar, venue vs vendor differ in change handler
  const filterBarRegionOptions = useMemo(() => {
    if (isVenue) {
      if (localRegionOptions.length > 0 || regionSlug || countrySlug) {
        return localRegionOptions.map(r => ({ value: r.slug, label: r.name }));
      }
      return null;
    }
    // Vendors: use region options already extracted from data (after this useMemo)
    //, resolved below after vendorFilterOptions is available; see filterBarVendorRegionOpts
    return null;
  }, [isVenue, localRegionOptions, regionSlug, countrySlug]);

  const vendorFilterOptions = useMemo(() => {
    if (!isVendor || !vendorFilterConfig) return {};
    const opts = extractFilterOptions(listings, vendorFilterConfig.filters, allCountryRegions);
    // Fallback rules: sort specialties by frequency, cap at 12
    for (const dim of vendorFilterConfig.filters) {
      if (dim.dataField?.endsWith("[]") && opts[dim.key]) {
        // Count frequency for sorting
        const freq = {};
        for (const v of listings) {
          const field = dim.dataField.replace("[]", "");
          const arr = v[field];
          if (Array.isArray(arr)) {
            for (const s of arr) {
              const trimmed = String(s).trim();
              freq[trimmed] = (freq[trimmed] || 0) + 1;
            }
          }
        }
        // Sort by frequency descending, cap at 12
        opts[dim.key] = opts[dim.key]
          .sort((a, b) => (freq[b] || 0) - (freq[a] || 0))
          .slice(0, 12);
      }
    }
    return opts;
  }, [isVendor, vendorFilterConfig, listings, allCountryRegions]);

  // Listing profile for data-driven suggestion pills in AICommandBar
  // Venue style key is "venueType"; price key is "priceRange", matches filterConfig.js
  const listingHints = useMemo(() => {
    const styleOpts = vendorFilterOptions?.venueType  || [];
    const priceOpts = vendorFilterOptions?.priceRange || [];
    return {
      dominantStyle: styleOpts[0] || null,
      dominantPrice: priceOpts[0] || null,
    };
  }, [vendorFilterOptions]);

  // Region options for vendors (computed after vendorFilterOptions)
  const filterBarVendorRegionOpts = useMemo(() => {
    if (isVenue) return null;
    const opts = vendorFilterOptions?.region;

    if (opts?.length) {
      // Prefer data-extracted regions, they reflect what's actually in the listings.
      return opts.map(o => {
        // getRegionBySlug falls back to REGIONS[0] (slug="all", name="All Regions") when
        // the slug isn't found, must check for the sentinel before using its name.
        const r = getRegionBySlug(o);
        if (r && r.slug !== 'all') return { value: o, label: r.name };
        // Country-level slugs (england, italy, uae…) live in COUNTRIES, not REGIONS
        const c = getCountryBySlug(o);
        if (c?.name) return { value: o, label: c.name };
        // Last resort: capitalise the slug
        const label = o.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        return { value: o, label };
      });
    }

    // Fallback: use geo.js static regions just like venues do, so the dropdown
    // never disappears just because no listings have regionSlug populated yet.
    if (localRegionOptions.length > 0 || regionSlug || countrySlug) {
      return localRegionOptions.map(r => ({ value: r.slug, label: r.name }));
    }

    return null;
  }, [isVenue, vendorFilterOptions, localRegionOptions, regionSlug, countrySlug]);

  // ── Region navigation: selecting a region in vendor filter → URL change ────
  const handleVendorRegionNavigate = useCallback((regionValue) => {
    if (!regionValue || regionValue === "All" || regionValue === "all") {
      // "All" → always go fully global, clearing both country and region scope
      onViewRegionCategory(null, null, categorySlug);
      return;
    }

    if (countrySlug) {
      // Country-scoped page: find region within this country by slug OR name
      const regions = getRegionsByCountry(countrySlug);
      const match = regions.find(r => r.slug === regionValue || r.name === regionValue);
      if (match) onViewRegionCategory(countrySlug, match.slug, categorySlug);
    } else {
      // Global page (no country in URL): look up region across ALL countries
      const region = getRegionBySlug(regionValue);
      if (region && region.slug !== "all" && region.countrySlug) {
        onViewRegionCategory(region.countrySlug, region.slug, categorySlug);
      }
    }
  }, [countrySlug, categorySlug, onViewRegionCategory]);

  // ── Filter transition feedback, smooth fade when results change ──────────
  // Trigger a brief opacity transition to signal that filtering just happened
  useEffect(() => {
    setIsFilteringTransition(true);
    const timer = setTimeout(() => setIsFilteringTransition(false), 100);
    return () => clearTimeout(timer);
  }, [finalListings]);

  // ── Related categories (sibling categories in same region) ────────────────
  const siblingCategories = useMemo(
    () => VENDOR_CATEGORIES.filter((vc) => vc.slug !== categorySlug),
    [categorySlug],
  );

  // ── Alternative categories (for empty/low result UX) ──────────────────────
  // Compute result counts for all categories to suggest when current is empty
  const alternativeCategories = useMemo(() => {
    const alternatives = [];
    VENDOR_CATEGORIES.forEach((vc) => {
      if (vc.slug === categorySlug) return; // Skip current category

      // Apply same filters but with this category
      const categoryListings = listings.filter((v) => {
        const catMatch = !filters.category || v.category === filters.category || vc.slug === "wedding-venues";
        const regionMatch = !filters.region || v.region === filters.region || v.regionSlug === filters.region;
        return catMatch && regionMatch;
      });

      if (categoryListings.length > 0) {
        alternatives.push({
          category: vc.label,
          slug: vc.slug,
          count: categoryListings.length,
        });
      }
    });

    // Sort by count descending, take top 6
    return alternatives.sort((a, b) => b.count - a.count).slice(0, 6);
  }, [listings, filters.category, filters.region, categorySlug]);

  // ── Related regions (same category in nearby regions) ─────────────────────
  const relatedRegions = useMemo(() => {
    if (!region?.relatedRegionSlugs?.length) return [];
    const allRegions = countrySlug ? getRegionsByCountry(countrySlug) : [];
    return region.relatedRegionSlugs
      .map((slug) => allRegions.find((r) => r.slug === slug))
      .filter(Boolean)
      .slice(0, 4);
  }, [region, countrySlug]);

  // ── Scroll & load-in ──────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Venue filter change handler, delegates to unified vendorFilters.
  // On global routes (countrySlug=null), intercepts country slugs from AI intent
  // so "italy" in a query updates the country dropdown instead of an unknown filter.
  // ── AI intent handler, fetches behavioral signals when intent resolves ──────
  const handleAIIntent = useCallback((parsed) => {
    setAiIntent(parsed);
    setEntityFallback(null);
    if (parsed) {
      fetchAISignals(categorySlug || "wedding-venues")
        .then(setAiSignals)
        .catch(() => {});
    } else {
      setAiSignals({});
    }
  }, [categorySlug]);

  // ── Click wrapper, tracks ai_result_click when AI search is active ──────────
  const handleViewVenue = useCallback((v) => {
    if (aiIntent) {
      track("ai_result_click", {
        vendor_id: v.id,
        category:  categorySlug || "wedding-venues",
        country:   countrySlug  || v.countrySlug || null,
        region:    regionSlug   || v.regionSlug  || null,
        query_styles:   aiIntent.styles   || [],
        query_capacity: aiIntent.capacity || null,
      });
    }
    onViewVenue(v);
  }, [aiIntent, categorySlug, countrySlug, regionSlug, onViewVenue]);

  const COUNTRY_SLUGS = useMemo(() => new Set(COUNTRIES.map(c => c.slug)), []);
  const handleVenueFiltersChange = useCallback((f) => {
    setNameMatches(null);
    setEntityFallback(null);
    if (!countrySlug && f.region && COUNTRY_SLUGS.has(f.region)) {
      setLocalCountryFilter(f.region);
      setLocalRegionFilter('all');
      setVendorFilters({ ...f, region: 'all' });
    } else {
      setVendorFilters(f);
    }
  }, [setVendorFilters, countrySlug, COUNTRY_SLUGS]);

  // ── Handle alternative category selection from empty result state ─────────
  const handleSelectAlternative = useCallback((altSlug) => {
    const path = getRegionCategoryPath(countrySlug || "italy", regionSlug || "all", altSlug);
    // Navigate to the new category
    if (onViewRegionCategory) {
      onViewRegionCategory(countrySlug || "italy", regionSlug || "all", altSlug);
    }
  }, [countrySlug, regionSlug, onViewRegionCategory]);

  // ── Sparse zone detection: < 3 pins visible for active category ────────────
  const handleSparsePins = useCallback((data) => {
    const { category, count } = data;
    if (count < 3) {
      // Find best nearby region suggestion (prefer regions with more listings)
      let nearbyRegionUrl = null;
      let nearbyRegionName = null;

      if (relatedRegions && relatedRegions.length > 0) {
        // Suggest the first related region (sorted by relevance in geo data)
        const suggestedRegion = relatedRegions[0];
        if (suggestedRegion) {
          nearbyRegionName = suggestedRegion.name;
          nearbyRegionUrl = getRegionCategoryPath(countrySlug, suggestedRegion.slug, category);
        }
      }

      const categoryLabel = category === "wedding-venues" ? "venue" : category.replace(/-/g, " ");
      const nearbyText = nearbyRegionName ? ` Try <strong>${nearbyRegionName}</strong> nearby.` : " Try nearby regions for more options.";

      setSparseZoneAlert({
        category,
        count,
        nearbyRegionUrl,
        nearbyRegionName,
        message: `Only ${count} ${categoryLabel}${count !== 1 ? "s" : ""} here.${nearbyText}`,
      });
    } else {
      setSparseZoneAlert(null);
    }
  }, [relatedRegions, categorySlug, countrySlug]);

  // ── Search submit → route to CategoryPage ─────────────────────────────────
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchInput.trim();
    if (!q) return;
    onViewCategory({
      countrySlug,
      regionSlug,
      searchQuery: q,
    });
  };

  const toggleSave = useCallback(
    (id) =>
      setSavedIds((s) =>
        s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
      ),
    [],
  );

  const searchLabel = countrySlug === "uk" ? "UK Search" : "Search";
  const searchPlaceholder = `Search ${categoryLabel.toLowerCase()}${regionName ? ` in ${regionName}` : ""}…`;

  // ── Canonical path for SEO panel ──────────────────────────────────────────
  const cityName = citySlug
    ? citySlug.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
    : null;
  const canonicalPath = citySlug && countrySlug && regionSlug && categorySlug
    ? `/${countrySlug}/${regionSlug}/${citySlug}/${categorySlug}`
    : getRegionCategoryPath(countrySlug, regionSlug, categorySlug);

  // ── AI-DRIVEN SEO SYSTEM (synchronous, cached, zero render-blocking) ───────
  // Generate SEO metadata, internal linking clusters, and varied content
  const aiSEO = useMemo(() => {
    return getLocationSEOSync({
      locationName: regionName || countryName || "Wedding Destinations",
      countryName: countryName || "Global",
      regionType: regionSlug ? (regionSlug.includes("-") && countrySlug ? "region" : "city") : "country",
      venueCount: listingCount,
      locationDescription: editorial,
      nearbyLocations: generateNearbyLocations(countrySlug, regionSlug, categorySlug),
    });
  }, [regionName, countryName, regionSlug, countrySlug, categorySlug, listingCount, editorial]);

  // ── SEO Metadata (with fallback to AI-generated if needed) ────────────────
  // ── Search Identity Engine (Pass 3): single source of truth for title / description /
  //    canonical. Deterministic, length-disciplined, collision-free, self-canonical.
  //    Classifies region-category (3-seg) vs city-category (4-seg) from the path itself. ──
  const _identity = resolveIdentity(canonicalPath);
  const canonicalUrl = _identity.canonical;
  const pageTitle = _identity.title;          // already brand-suffixed + ≤60; do NOT append brand again
  const metaDescription = _identity.description;

  // ── Breadcrumb schema ───────────────────────────────────────────────────────
  const breadcrumbItems = useMemo(() => {
    const items = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://luxuryweddingdirectory.com/"
      }
    ];
    let position = 2;

    // Add country (if present)
    if (countrySlug && country) {
      items.push({
        "@type": "ListItem",
        "position": position,
        "name": country.name,
        "item": `https://luxuryweddingdirectory.com/${countrySlug}`
      });
      position++;
    }

    // Add region (if present and different from country)
    if (regionSlug && region && regionName !== countryName) {
      items.push({
        "@type": "ListItem",
        "position": position,
        "name": regionName,
        "item": `https://luxuryweddingdirectory.com/${countrySlug}/${regionSlug}`
      });
      position++;
    }

    // Add city (if city-level page)
    if (citySlug && cityName) {
      items.push({
        "@type": "ListItem",
        "position": position,
        "name": cityName,
        "item": `https://luxuryweddingdirectory.com/${countrySlug}/${regionSlug}/${citySlug}`
      });
      position++;
    }

    // Add category (current page)
    items.push({
      "@type": "ListItem",
      "position": position,
      "name": categoryLabel,
      "item": canonicalUrl
    });

    // Strip the URL from any level the public site will not serve, keeping the
    // level itself as a name-only entry.
    //
    // Audited 2026-08-15: this page declared /usa/california and
    // /usa/california/beverly-hills in its BreadcrumbList, and both return 404
    // because California is a held region. The trail was telling Google that two
    // pages exist in the hierarchy which do not. Same defect the shared
    // buildBreadcrumbSchema had, and this template is a SECOND copy of the rule
    // rather than a caller of it, which is exactly how the two drifted.
    //
    // Keeping the name preserves the true hierarchy ("California is the region")
    // while asserting no URL. This array drives the VISIBLE trail, where showing
    // "Kent" as plain text is correct and useful to a reader. The JSON-LD takes a
    // stricter projection of it; see breadcrumbSchemaItems below.
    return items.map((it) => {
      if (!it.item) return it;
      const p = String(it.item).replace("https://luxuryweddingdirectory.com", "") || "/";
      if (isPubliclyRoutable(p)) return it;
      const { item, ...nameOnly } = it;
      return nameOnly;
    });
  }, [countrySlug, regionSlug, citySlug, cityName, country, region, regionName, countryName, categoryLabel, canonicalUrl]);

  // ── Breadcrumb schema projection ────────────────────────────────────────────
  // The JSON-LD gets a STRICTER view of the same array. Google requires `item` on
  // every ListItem except the last, and when one is missing it rejects the ENTIRE
  // BreadcrumbList rather than that rung. Search Console flagged exactly these four
  // URLs on 18 Aug 2026 with "Missing field 'item'":
  //   /st-lucia/castries/marigot-bay/wedding-venues, /st-lucia/castries/wedding-venues,
  //   /bahamas/exuma/wedding-venues, /maldives/south-male-atoll/wedding-venues
  //
  // So a held level is DROPPED from the schema, and positions renumber over the
  // survivors because a gap in position (1, 2, 4) is separately invalid.
  //
  // This is deliberately a projection, not a second array built from scratch. The
  // 2026-08-15 audit found the visual trail, the HTML links and the schema could
  // each say something different, and one source is the fix. That still holds: the
  // human trail and the machine trail are the same data, and the machine one omits
  // rungs it is not allowed to describe. A reader benefits from seeing "Kent"; a
  // crawler is forbidden from being told about it without a URL.
  const breadcrumbSchemaItems = useMemo(() => {
    const survivors = breadcrumbItems.filter(
      (it, i) => i === breadcrumbItems.length - 1 || !!it.item
    );
    return survivors.map((it, i) => ({ ...it, position: i + 1 }));
  }, [breadcrumbItems]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        {/* Noindex city pages with no listings, prevents thin-content penalties.
            Canonical points to parent region page so crawl equity consolidates upward. */}
        {citySlug && listingsLoaded && dbListings.length === 0 && (
          <meta name="robots" content="noindex, follow" />
        )}
        {citySlug && listingsLoaded && dbListings.length === 0 && countrySlug && regionSlug && categorySlug && (
          <link rel="canonical" href={`https://luxuryweddingdirectory.com/${countrySlug}/${regionSlug}/${categorySlug}`} />
        )}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={heroImg} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image"       content={heroImg} />

        {/* BreadcrumbList Schema, route-derived only (absolute URLs, real geography).
            The aiSEO breadcrumb is intentionally NOT used: it emitted relative @id
            URLs and a hallucinated "Global" country when countryName was unresolved
            (e.g. /global/tyrol on an Austria page). Breadcrumbs are deterministic
            from the URL, so the local list is always correct. */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbSchemaItems,
          })}
        </script>

        {/* Place Schema, aiSEO version preferred; coordinate fallback when absent.
            Geography (areaServed + url) is route-derived, overriding the aiSEO
            values: those are built from display names slugified, which produced
            "Global" / "/global/tyrol" (and would produce "/austria/tyrol-&-innsbruck")
            whenever the country/region names were unresolved. Slugs are the truth. */}
        {(() => {
          let placeSchema = aiSEO?.schemas?.place || (
            region?.lat != null && region?.lng != null ? {
              "@context": "https://schema.org",
              "@type": "Place",
              "name": regionName,
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": region.lat,
                "longitude": region.lng,
              },
            } : null
          );
          if (placeSchema && countrySlug) {
            placeSchema = {
              ...placeSchema,
              "url": `https://luxuryweddingdirectory.com/${countrySlug}${regionSlug ? `/${regionSlug}` : ""}`,
            };
            if (countryName) placeSchema.areaServed = { "@type": "Country", "name": countryName };
            else delete placeSchema.areaServed;
          }
          return placeSchema ? (
            <script type="application/ld+json">
              {JSON.stringify(placeSchema)}
            </script>
          ) : null;
        })()}

        {/* FAQ Schema, from AI SEO system */}
        {aiSEO?.schemas?.faqPage && (
          <script type="application/ld+json">
            {JSON.stringify(aiSEO.schemas.faqPage)}
          </script>
        )}
      </Helmet>
      {/* CollectionPage Schema — rendered INLINE in the body, not via Helmet. As the last of
          several <script type="application/ld+json"> children, react-helmet-async
          intermittently dropped it during reconciliation, so the block was present in some
          prerenders and absent in others (structured-data drift). Rendering it inline ties it
          to the synchronous React commit → deterministically present once the page renders.
          JSON-LD is valid anywhere in the document, so crawlers read it here identically. */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": pageTitle,
        "description": metaDescription,
        "url": canonicalUrl,
        ...(dbListings.length > 0 ? { "numberOfItems": dbListings.length } : {}),
      }) }} />
      <ThemeCtx.Provider value={C}>
      <div style={{ background: C.black, minHeight: isMobile ? "auto" : "100vh", color: C.white, overflowX: "clip" }}>

        {/* ════════════════════════════════════════════════════════════════════
            1. NAV, main site navigation
        ════════════════════════════════════════════════════════════════════ */}
        <HomeNav
          hasHero={true}
          heroImg={heroImg}
          darkMode={darkMode}
          onToggleDark={themeCtx.toggleDark}
          onNavigateStandard={() => onBackHome()}
          onNavigateAbout={() => onBackHome()}
        />

        {/* ════════════════════════════════════════════════════════════════════
            2. HERO, 50vh
        ════════════════════════════════════════════════════════════════════ */}
        <section
          aria-label={`${categoryLabel}${regionName ? ` in ${regionName}` : ""}`}
          style={{
            position: "relative",
            height: "50vh",
            minHeight: 700,
            overflow: "hidden",
            background: "#0a0806",
          }}
        >
          {/* Background images, crossfade slideshow */}
          {heroImages.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={i === 0 ? `${categoryLabel}${regionName ? ` in ${regionName}` : ""}` : ""}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: heroSlideIdx === i ? 0.5 : 0,
                transform: heroSlideIdx === i ? "scale(1.04)" : "scale(1.0)",
                transition: "opacity 1.8s ease, transform 8s ease",
                willChange: "opacity, transform",
              }}
            />
          ))}

          {/* Gradient overlays */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg,rgba(4,3,2,0.45) 0%,rgba(4,3,2,0.25) 40%,rgba(4,3,2,0.8) 100%)",
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(90deg,rgba(4,3,2,0.6) 0%,transparent 60%)",
            }}
          />

          {/* Gold shimmer bar */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              background: "linear-gradient(90deg,#d39a21,#e8c97a,#d39a21)",
              backgroundSize: "200% 100%",
              animation: "shimmer 3s linear infinite",
              zIndex: 2,
            }}
          />

          {/* Hero content */}
          <div
            className="lwd-rc-hero-content"
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              padding: "0 80px 24px",
              opacity: loaded ? 1 : 0,
              transform: loaded ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.9s ease",
            }}
          >
            {/* Category label */}
            <div
              style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}
              aria-hidden="true"
            >
              <div style={{ width: 32, height: 1, background: "rgba(211,154,33,0.6)" }} />
              <span
                style={{
                  fontSize: 10,
                  letterSpacing: "4px",
                  textTransform: "uppercase",
                  color: "rgba(211,154,33,0.9)",
                  fontFamily: NU,
                  fontWeight: 600,
                }}
              >
                {categoryIcon} {categoryLabel}
              </span>
            </div>

            {/* Title + count badge */}
            <div style={{ display: "flex", alignItems: "baseline", gap: 20, marginBottom: 16, flexWrap: "wrap" }}>
              <h1
                style={{
                  fontFamily: GD,
                  fontSize: "clamp(36px,5vw,62px)",
                  fontWeight: 400,
                  color: "#fff",
                  lineHeight: 1.05,
                  letterSpacing: "-0.5px",
                  margin: 0,
                }}
              >
                {categoryLabel.endsWith(' Collective') ? (
                  <>
                    {categoryLabel.slice(0, -' Collective'.length)}
                    <br />
                    <em style={{ fontStyle: "italic", color: "rgba(211,154,33,0.95)" }}>Collective</em>
                  </>
                ) : categoryLabel.endsWith(' Creatives') ? (
                  <>
                    {categoryLabel.slice(0, -' Creatives'.length)}
                    <br />
                    <em style={{ fontStyle: "italic", color: "rgba(211,154,33,0.95)" }}>Creatives</em>
                  </>
                ) : categoryLabel}
                {regionName && (
                  <>{" "}in{" "}<em style={{ fontStyle: "italic", color: "#d1a352" }}>{regionName}</em></>
                )}
              </h1>
            </div>

            {/* Subtitle, unique, max 160 chars, distinct from editorial body */}
            {heroSubtitle && (
            <p
              style={{
                fontSize: 16,
                color: "rgba(255,255,255,0.65)",
                fontFamily: NU,
                fontWeight: 300,
                lineHeight: 1.6,
                maxWidth: 560,
                marginBottom: 32,
              }}
            >
              {heroSubtitle}
            </p>
            )}

            {/* AI Search trigger */}
            <button
              onClick={() => setImmersiveOpen(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(211,154,33,0.12)",
                border: "1px solid rgba(211,154,33,0.38)",
                borderRadius: 3,
                color: "#d39a21",
                fontFamily: NU,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                padding: "9px 20px",
                cursor: "pointer",
                marginBottom: 28,
                transition: "all 0.2s ease",
                alignSelf: "flex-start",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(211,154,33,0.2)"; e.currentTarget.style.borderColor = "#d39a21"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(211,154,33,0.12)"; e.currentTarget.style.borderColor = "rgba(211,154,33,0.38)"; }}
            >
              <span style={{ fontSize: 13 }}>✦</span>
              Explore with Aura
              <span style={{ fontSize: 13 }}>✦</span>
            </button>

            {/* Stats row */}
            <div
              className="lwd-rc-hero-stats"
              style={{ display: "flex", gap: 32, alignItems: "center" }}
              aria-label="Key statistics"
            >
              {[
                { val: listingCount > 0 ? listingCount : "-", label: categorySlug === "wedding-venues" ? "Curated Venues" : "Curated Listings" },
                ...(regionName ? [{ val: regionName, label: "Region", isText: true }] : []),
                {
                  val: listingCount > 0 ? "100%" : "Coming Soon",
                  label: listingCount > 0 ? "Personally Verified" : "Listings",
                  soft: listingCount === 0,
                },
              ].map((s, i) => (
                <div
                  key={i}
                  style={{
                    borderLeft: i > 0 ? "1px solid rgba(255,255,255,0.15)" : "none",
                    paddingLeft: i > 0 ? 32 : 0,
                  }}
                >
                  <div
                    style={{
                      fontFamily: s.isText ? NU : GD,
                      fontSize: s.soft ? 18 : s.isText ? 16 : 28,
                      fontWeight: s.soft ? 300 : s.isText ? 600 : 600,
                      color: s.soft ? "rgba(255,255,255,0.45)" : "#d39a21",
                      lineHeight: 1,
                      opacity: s.soft ? 0.6 : 1,
                    }}
                  >
                    {s.val}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      letterSpacing: "2px",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.45)",
                      marginTop: 4,
                      fontFamily: NU,
                    }}
                  >
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Breadcrumb trail, hidden on mobile.
                Rendered FROM `breadcrumbItems`, the same array that feeds the
                BreadcrumbList schema above. That is deliberate: the audit of
                2026-08-15 found the visual trail, the HTML links and the schema
                could each say something different, and the only durable fix is one
                source rather than three that must be kept in step.

                So a level is a real <a href> exactly when the schema gives it an
                `item`, which is exactly when the routability gate says the page is
                servable. A held level (Kent, California) renders as plain text in
                both places. The last crumb is the current page and is never a link.

                onClick preserves in-app navigation; modified clicks fall through to
                the browser so these behave like the real links they now are. */}
            <nav
              aria-label="Breadcrumb"
              style={{
                marginTop: 48,
                display: isMobile ? "none" : "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 11,
                color: "rgba(255,255,255,0.45)",
                letterSpacing: "0.5px",
                fontFamily: NU,
              }}
            >
              {breadcrumbItems.map((crumb, i) => {
                const isLast = i === breadcrumbItems.length - 1;
                const path = crumb.item
                  ? String(crumb.item).replace("https://luxuryweddingdirectory.com", "") || "/"
                  : null;
                const handler =
                  path === "/" ? onBackHome
                  : (path && countrySlug && path === `/${countrySlug}`) ? () => onViewCountry(countrySlug)
                  : (path && regionSlug && path === `/${countrySlug}/${regionSlug}`) ? onBack
                  : null;
                const base = {
                  fontSize: 11,
                  fontFamily: NU,
                  letterSpacing: "0.5px",
                  color: isLast ? "rgba(211,154,33,0.9)" : "rgba(255,255,255,0.45)",
                  fontWeight: isLast ? 600 : 400,
                  textDecoration: "none",
                };
                return [
                  i > 0 ? <span key={`sep-${crumb.position}`} style={{ opacity: 0.4 }}>›</span> : null,
                  path && !isLast ? (
                      <a
                        href={path}
                        onClick={(e) => {
                          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                          if (!handler) return;
                          e.preventDefault();
                          handler();
                        }}
                      key={`c-${crumb.position}`}
                      style={{ ...base, cursor: "pointer" }}
                    >
                      {crumb.name}
                    </a>
                  ) : (
                    <span key={`c-${crumb.position}`} style={base}>{crumb.name}</span>
                  ),
                ];
              })}
            </nav>
          </div>
        </section>



        {/* Featured section removed, unified into main grid below */}

        {/* ════════════════════════════════════════════════════════════════════
            6. REAL WEDDINGS SECTION (Wedding Venues Only)
        ════════════════════════════════════════════════════════════════════ */}
        {/* Real Weddings, section only renders if component has content */}


        {/* ════════════════════════════════════════════════════════════════════
            7. AI SEARCH + FILTER, always shown regardless of listing count
        ════════════════════════════════════════════════════════════════════ */}
        <div style={{ background: darkMode ? C.dark : "#f2f0ea" }}>
            {/* ═══ AURA CONCIERGE BANNER ═══ */}
            {auraSummary && !summaryDismissed && (
              <div style={{
                background:   darkMode ? "rgba(211,154,33,0.06)" : "rgba(211,154,33,0.07)",
                borderLeft:   "3px solid rgba(211,154,33,0.45)",
                borderBottom: `1px solid ${darkMode ? "rgba(211,154,33,0.12)" : "rgba(211,154,33,0.18)"}`,
              }}>
                <div style={{
                  maxWidth:      1280,
                  margin:        "0 auto",
                  padding:       isMobile ? "14px 20px" : "16px 48px",
                  display:       "flex",
                  alignItems:    "flex-start",
                  justifyContent: "space-between",
                  gap:           16,
                }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                      <span style={{ color: "rgba(211,154,33,0.9)", fontSize: 9 }}>✦</span>
                      <span style={{
                        fontFamily:    NU,
                        fontSize:      9,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        color:         "rgba(211,154,33,0.7)",
                        fontWeight:    700,
                      }}>
                        Curated for you
                      </span>
                    </div>
                    <p style={{
                      fontFamily:    GD,
                      fontSize:      isMobile ? 14 : 15,
                      fontWeight:    300,
                      fontStyle:     "italic",
                      color:         C.off,
                      margin:        0,
                      letterSpacing: "0.01em",
                      lineHeight:    1.45,
                    }}>
                      {auraSummary && listingCount > 0
                        ? `${auraSummary} Showing ${listingCount} ${listingCount === 1 ? "result" : "results"}.`
                        : auraSummary
                      }
                    </p>
                  </div>
                  <button
                    onClick={() => setSummaryDismissed(true)}
                    aria-label="Dismiss"
                    style={{
                      background:  "none",
                      border:      "none",
                      cursor:      "pointer",
                      color:       darkMode ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)",
                      fontSize:    16,
                      lineHeight:  1,
                      padding:     "2px 4px",
                      flexShrink:  0,
                      marginTop:   2,
                      transition:  "color 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = darkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)"}
                    onMouseLeave={e => e.currentTarget.style.color = darkMode ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)"}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            {/* ═══ AURA CROSS-CATEGORY NAVIGATION SUGGESTION ═══ */}
            {auraCrossNav && (
              <div style={{
                background:   darkMode ? "rgba(211,154,33,0.05)" : "rgba(211,154,33,0.06)",
                borderLeft:   "3px solid rgba(211,154,33,0.55)",
                borderBottom: `1px solid ${darkMode ? "rgba(211,154,33,0.1)" : "rgba(211,154,33,0.15)"}`,
              }}>
                <div style={{
                  maxWidth:       1280,
                  margin:         "0 auto",
                  padding:        isMobile ? "14px 20px" : "16px 48px",
                  display:        "flex",
                  alignItems:     "center",
                  justifyContent: "space-between",
                  gap:            16,
                  flexWrap:       "wrap",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                    <span style={{ color: "rgba(211,154,33,0.85)", fontSize: 10, flexShrink: 0 }}>✦</span>
                    <span style={{
                      fontFamily:    GD,
                      fontSize:      isMobile ? 13 : 14,
                      fontWeight:    300,
                      fontStyle:     "italic",
                      color:         C.off,
                      letterSpacing: "0.01em",
                    }}>
                      Looking for{" "}
                      <span style={{ fontWeight: 500 }}>{auraCrossNav.label}</span>
                      {auraCrossNav.region ? ` in ${auraCrossNav.region}` : ""}?
                    </span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                    <a
                      href={auraCrossNav.url}
                      style={{
                        display:       "inline-flex",
                        alignItems:    "center",
                        gap:           6,
                        fontFamily:    NU,
                        fontSize:      9,
                        fontWeight:    700,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color:         "rgba(211,154,33,0.9)",
                        background:    "rgba(211,154,33,0.1)",
                        border:        "1px solid rgba(211,154,33,0.35)",
                        borderRadius:  4,
                        padding:       "7px 14px",
                        textDecoration: "none",
                        transition:    "all 0.2s",
                        whiteSpace:    "nowrap",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = "rgba(211,154,33,0.18)"; e.currentTarget.style.borderColor = "rgba(211,154,33,0.6)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "rgba(211,154,33,0.1)"; e.currentTarget.style.borderColor = "rgba(211,154,33,0.35)"; }}
                    >
                      View {auraCrossNav.label} →
                    </a>
                    <button
                      onClick={() => setAuraCrossNav(null)}
                      aria-label="Dismiss"
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: darkMode ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)",
                        fontSize: 16, lineHeight: 1, padding: "2px 4px",
                        transition: "color 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = darkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)"}
                      onMouseLeave={e => e.currentTarget.style.color = darkMode ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)"}
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ═══ AI COMMAND BAR + FILTER BAR, same as RegionPage ═══ */}
            {!isMobile && (
            <section
              className="lwd-rc-section lwd-ai-command-section"
              style={{
                background: darkMode ? C.dark : "#d8d0c3",
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <AICommandBar
                countrySlug={countrySlug}
                countryName={countryName}
                regionSlug={regionSlug}
                regionName={regionName}
                categorySlug={categorySlug}
                entityType={isVenue ? "venue" : "vendor"}
                availableRegions={[
                  ...(vendorFilterOptions.region || []).map(l => ({ name: l, slug: l.toLowerCase().replace(/\s+/g, "-") })),
                  ...(!countrySlug ? (() => { const s = new Set(dbListings.map(l => l.countrySlug || l.country_slug).filter(Boolean)); return COUNTRIES.filter(c => s.has(c.slug)).map(c => ({ name: c.name, slug: c.slug })); })() : []),
                ]}
                filters={vendorFilters}
                onFiltersChange={handleVenueFiltersChange}
                defaultFilters={buildInitialFilters(vendorFilterConfig, initialRegionName)}
                onSummary={(s) => { setAuraSummary(s || null); setSummaryDismissed(false); }}
                onClearSummary={() => { setAuraSummary(null); setAuraMapFilter(null); setAuraCrossNav(null); }}
                onCategoryIntent={(cat) => {
                  if (!cat) { setAuraMapFilter(null); setAuraCrossNav(null); return; }
                  if (cat !== categorySlug) {
                    // Cross-category intent, suggest navigation, don't filter current page
                    const CAT_LABELS = {
                      "wedding-venues":   "Wedding Venues",
                      "photographers":    "Photographers",
                      "wedding-planners": "Wedding Planners",
                      "florists":         "Florists",
                      "videographers":    "Videographers",
                      "caterers":         "Caterers",
                      "musicians":        "Musicians",
                      "hair-beauty":      "Hair & Beauty",
                    };
                    const label = CAT_LABELS[cat] || cat;
                    const url   = getRegionCategoryPath(countrySlug, regionSlug || "all", cat);
                    setAuraCrossNav({ label, url, region: regionName || countryName });
                    setAuraMapFilter(null);
                  } else {
                    // Same-category, treat as filter intent
                    setAuraMapFilter(cat);
                    setAuraCrossNav(null);
                  }
                }}
                onMapIntent={(open) => {
                  if (open && isMobile) {
                    setMapOpen(true);          // mobile → bottom sheet
                  } else if (open && !mapOn) {
                    setMapOn(true);            // desktop → split layout
                  }
                }}
                onAIIntent={handleAIIntent}
                onNameMatch={setNameMatches}
                onEntityFallback={setEntityFallback}
                listingHints={listingHints}
              />
            </section>
            )}
        </div>{/* end section-7 background wrapper */}
        <div
          style={{
            position: "sticky",
            top: isMobile ? 0 : 'var(--nav-h, 64px)',
            zIndex: isMobile ? 250 : 100,
            background: darkMode ? C.dark : "#fff",
          }}
        >
          <FilterBar
            config={vendorFilterConfig}
            filters={vendorFilters}
            onFilterChange={(key, val) => setVendorFilters(prev => ({ ...prev, [key]: val }))}
            filterOptions={vendorFilterOptions}
            countryOptions={!countrySlug
              ? (() => {
                  // British/Irish nations sit below international destinations when they appear
                  const SECONDARY = new Set(['scotland', 'wales', 'northern-ireland', 'ireland']);
                  const slugsWithListings = new Set(dbListings.map(l => l.countrySlug || l.country_slug).filter(Boolean));
                  const opts = COUNTRIES
                    .filter(c => slugsWithListings.has(c.slug))
                    .map(c => ({ value: c.slug, label: c.name, secondary: SECONDARY.has(c.slug) }));
                  const primary   = opts.filter(o => !o.secondary);
                  const secondary = opts.filter(o =>  o.secondary);
                  return [...primary, ...secondary].map(({ value, label }) => ({ value, label }));
                })()
              : null}
            countryValue={localCountryFilter}
            onCountryChange={v => { setLocalCountryFilter(v); setLocalRegionFilter('all'); }}
            regionOptions={isVenue ? filterBarRegionOptions : filterBarVendorRegionOpts}
            regionValue={isVenue
              ? localRegionFilter
              : (vendorFilters.region && vendorFilters.region !== 'All' ? vendorFilters.region : 'all')}
            onRegionChange={isVenue ? setLocalRegionFilter : handleVendorRegionNavigate}
            count={listingCount}
            countLabel={categoryLabel ? categoryLabel.toLowerCase() : 'wedding venues'}
            viewMode={viewMode}
            onViewMode={handleViewMode}
            mapOn={mapOn}
            onToggleMap={handleToggleMap || null}
            isMobile={isMobile}
            dark={darkMode}
          />
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            8. LISTINGS OR COMING SOON
            Filter/search above always renders. Only the results are gated.
        ════════════════════════════════════════════════════════════════════ */}
        {/* ── Listings fetch error banner ─────────────────────────────────── */}
        {listingsError && (
          <div style={{
            margin: '16px 24px',
            padding: '14px 18px',
            background: 'rgba(220,38,38,0.08)',
            border: '1px solid rgba(220,38,38,0.25)',
            borderRadius: 6,
            fontSize: 13,
            color: '#dc2626',
            fontFamily: "'Nunito', sans-serif",
          }}>
            ⚠ Could not load listings, {listingsError}
          </div>
        )}

        {listingCount > 0 ? (
          <>
            {/* ════════════════════════════════════════════════════════════════════
                LISTINGS, EXPLORE LAYOUT (map on · desktop) or NORMAL SECTION
                Explore layout: any category, map on, desktop only.
                Normal section: map off, mobile, or non-venue categories.
            ════════════════════════════════════════════════════════════════════ */}

            {/* ── EXPLORE LAYOUT ── map on · desktop · any category ───────────── */}
            {mapOn && !isMobile && (
              <div
                aria-label={`${categoryLabel} listings`}
                style={{
                  display:      "flex",
                  height:       "calc(100vh - 72px)",
                  overflow:     "hidden",
                  background:   darkMode ? C.dark : "#f2f0ea",
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                {/* Left: scrollable card panel, owns its own padding, no hacks needed */}
                <div style={{
                  flex:       "0 1 900px",
                  overflowY:  "auto",
                  padding:    "40px 20px 40px 85px",
                  opacity:    isFilteringTransition ? 0.7 : (mapTransitioning ? 0.55 : 1),
                  transition: "opacity 0.15s ease",
                }}>
                  {listingCount === 0 ? (
                    <EmptyResultState
                      resultCount={0}
                      categoryLabel={categoryLabel}
                      alternatives={alternativeCategories}
                      onSelectAlternative={handleSelectAlternative}
                      darkMode={darkMode}
                    />
                  ) : viewMode === "grid" ? (
                    /* Grid, 2-col when map on, 3-col when map off */
                    <div
                      className="lwd-venue-grid"
                      style={{
                        ...getGridStyles(false),
                        gridTemplateColumns: "repeat(2, 1fr)",
                      }}
                      aria-label={isVendor ? `${categoryLabel} grid` : "Venue grid"}
                    >
                      {visibleVenueListings.map((v, idx) => (
                        <div
                          key={v.id}
                          className="lwd-card-enter"
                          data-listing-id={v.id}
                          onMouseEnter={() => { setActiveListingId(v.id); PinSyncBus.emit("card:hover", v.id); }}
                          onMouseLeave={() => { setActiveListingId(null); PinSyncBus.emit("card:leave", v.id); }}
                          style={{
                            ...getCardWrapperStyles(false),
                            outline:      activeListingId === v.id ? "2px solid rgba(211,154,33,0.5)" : "none",
                            transition:   "outline 0.2s",
                          }}
                        >
                          {isVenue ? (
                            <LuxuryVenueCard
                              v={v}
                              onView={() => handleViewVenue(v)}
                              quickViewItem={qvItem}
                              setQuickViewItem={setQvItem}
                              matchedStyles={vendorFilters.style ? [vendorFilters.style] : []}
                              otherFilters={{ region: vendorFilters.region, capacity: vendorFilters.capacity }}
                              priority={idx === 0}
                            />
                          ) : vendorFilterConfig?.card === "PlannerCard" ? (
                            <PlannerCard v={v} mode="grid" onView={() => onViewPlanner(v)} isMobile={isMobile} />
                          ) : (
                            <LuxuryVendorCard
                              v={v}
                              onView={() => handleViewVendor(v)}
                              quickViewItem={qvItem}
                              setQuickViewItem={setQvItem}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* List, single column, full width */
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {visibleVenueListings.map((v) => (
                        <div
                          key={v.id}
                          data-listing-id={v.id}
                          onMouseEnter={() => { setActiveListingId(v.id); PinSyncBus.emit("card:hover", v.id); }}
                          onMouseLeave={() => { setActiveListingId(null); PinSyncBus.emit("card:leave", v.id); }}
                          onClick={() => PinSyncBus.emit("card:click", v.id)}
                        >
                          {vendorFilterConfig?.card === "PlannerCard" ? (
                            <PlannerCard v={v} mode="list" listMode onView={() => onViewPlanner(v)} isMobile={isMobile} isHighlighted={activeListingId === v.id} />
                          ) : (
                            <VenueListItemCard
                              v={v}
                              onView={() => handleViewVenue(v)}
                              isHighlighted={activeListingId === v.id}
                              quickViewItem={qvItem}
                              setQuickViewItem={setQvItem}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {hasMoreVenueCards && (
                    <LoadMoreVenuesButton C={C} onClick={() => setVisibleVenueCount(c => c + VENUE_CARDS_PAGE_SIZE)} />
                  )}

                  {/* Near match suggestions (when results are low) */}
                  {listingCount > 0 && listingCount <= 3 && (
                    <NearMatchSection
                      allListings={listings}
                      matchedStyles={vendorFilters.style ? [vendorFilters.style] : []}
                      primaryResults={finalListings}
                      onView={handleViewVenue}
                      darkMode={darkMode}
                      categoryLabel={categoryLabel}
                    />
                  )}
                </div>

                {/* Right: MASTERMap, naturally full height, zero hacks */}
                <div style={{
                  flex:       1,
                  position:   "relative",
                  opacity:    mapTransitioning ? 0 : 1,
                  transform:  mapTransitioning ? "translateX(24px)" : "translateX(0)",
                  transition: "opacity 0.3s ease, transform 0.3s ease-out",
                }}>
                  <MASTERMap
                    items={finalListings}
                    label={`${categoryLabel} · ${regionName || countryName || "Italy"}`}
                    viewMode={viewMode}
                    onToggleView={viewMode === "grid" ? handleToggleMap : () => handleViewMode("grid")}
                    countrySlug={countrySlug || "italy"}
                    // Match the destination pages exactly. LocationPage, CountryTemplate and
                    // USAPage all pass C.black, which is theme-aware (#000000 dark, #fbf7f4
                    // light); this passed a hardcoded #f2f0ea in light mode, so the map's
                    // left-edge gradient washed cream here and black on the destination pages
                    // even though both render the same CARTO dark_all tiles.
                    pageBg={C.black}
                    auraSummary={auraSummary && !summaryDismissed ? auraSummary : null}
                    activeFilter={auraMapFilter}
                    onFilterChange={(cat) => setAuraMapFilter(cat)}
                    auraRecommendedIds={auraRecommendedIds}
                    onSparsePins={handleSparsePins}
                    activeListingId={activeListingId}
                    showFollowToggle={true}
                    onPinClick={(listingId) => {
                      setActiveListingId(listingId);
                      const el = document.querySelector(`[data-listing-id="${listingId}"]`);
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
                    }}
                  />
                </div>
              </div>
            )}

            {/* ── ENTITY NOT-FOUND BANNER ── venue searched but not in directory ── */}
            {entityFallback && (
              <div style={{
                padding:   isMobile ? "24px 16px 0" : "32px 48px 0",
                maxWidth:  1280,
                margin:    "0 auto",
              }}>
                {/* Header */}
                <div style={{ marginBottom: 24 }}>
                  <p style={{
                    fontFamily:    "var(--font-body)",
                    fontSize:      11,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color:         "rgba(211,154,33,0.7)",
                    marginBottom:  10,
                  }}>
                    Not in our directory yet
                  </p>
                  <h3 style={{
                    fontFamily: "var(--font-heading-primary)",
                    fontSize:   isMobile ? 20 : 26,
                    fontWeight: 400,
                    color:      "#1a1816",
                    margin:     "0 0 8px",
                    letterSpacing: "-0.01em",
                  }}>
                    {entityFallback.venueName}
                  </h3>
                  <p style={{
                    fontFamily: "var(--font-body)",
                    fontSize:   14,
                    color:      "#8a857e",
                    margin:     0,
                    lineHeight: 1.5,
                  }}>
                    {entityFallback.city
                      ? `We don't have ${entityFallback.venueName} in our directory yet. Here are our top venues in ${entityFallback.city}.`
                      : `We don't have ${entityFallback.venueName} in our directory yet. Here are some venues you might love.`}
                  </p>
                </div>

                {/* Alternative venue cards */}
                {entityFallback.listings?.length > 0 && (
                  <div style={{
                    display:             "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                    gap:                 24,
                    marginBottom:        40,
                  }}>
                    {entityFallback.listings.map(v => (
                      <LuxuryVenueCard
                        key={v.id}
                        v={v}
                        onView={() => handleViewVenue(v)}
                        quickViewItem={qvItem}
                        setQuickViewItem={setQvItem}
                        matchedStyles={[]}
                        otherFilters={{}}
                      />
                    ))}
                  </div>
                )}

                <div style={{
                  borderTop:    "1px solid rgba(0,0,0,0.07)",
                  marginBottom: 32,
                }} />
              </div>
            )}

            {/* ── NAME MATCH BANNER ── shown when user searched a venue/brand name ── */}
            {nameMatches && nameMatches.length > 0 && (
              <div style={{
                padding:      isMobile ? "24px 16px" : "32px 48px",
                maxWidth:     1280,
                margin:       "0 auto",
              }}>
                <p style={{
                  fontFamily:   "var(--font-body)",
                  fontSize:     12,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color:         "rgba(211,154,33,0.8)",
                  marginBottom:  20,
                }}>
                  {nameMatches.length === 1 ? "Direct match" : `${nameMatches.length} venues found`}
                </p>
                <div style={{
                  display:               "grid",
                  gridTemplateColumns:   isMobile ? "1fr" : "repeat(3, 1fr)",
                  gap:                   24,
                }}>
                  {nameMatches.map(v => (
                    <div
                      key={v.id}
                      style={{ position: "relative" }}
                    >
                      {v.countrySlug && v.countrySlug !== countrySlug && (
                        <span style={{
                          position:     "absolute",
                          top:           10,
                          left:          10,
                          zIndex:        10,
                          background:    "rgba(0,0,0,0.65)",
                          color:         "#fff",
                          fontSize:       11,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          padding:       "3px 8px",
                          borderRadius:  3,
                        }}>
                          {v.region || v.countrySlug}
                        </span>
                      )}
                      <LuxuryVenueCard
                        v={v}
                        onView={() => handleViewVenue(v)}
                        quickViewItem={qvItem}
                        setQuickViewItem={setQvItem}
                        matchedStyles={[]}
                        otherFilters={{}}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── NORMAL SECTION ── map off · mobile · non-venue categories ─────── */}
            {(!mapOn || isMobile) && (
              <section
                className="lwd-rc-section"
                aria-label={`${categoryLabel} listings`}
                style={{
                  background:   "transparent",
                  padding:      isMobile ? "0 0 72px" : "40px 48px 72px",
                  borderBottom: "none",
                  maxWidth:     1280,
                  margin:       "0 auto",
                }}
              >
                {isMobile ? (
                  /* Mobile: responsive grid layout */
                  <>
                    {/* wedding-venues · grid view, mobile: responsive grid */}
                    {categorySlug === "wedding-venues" && viewMode === "grid" && listingCount > 0 && (
                      <div
                        className="lwd-venue-grid-mobile"
                        style={{
                          ...getGridStyles(true),
                          opacity: isFilteringTransition ? 0.7 : 1,
                          transition: "opacity 0.15s ease",
                        }}
                        aria-label="Venue grid"
                      >
                        {visibleVenueListings.map((v, idx) => (
                          <div
                            key={v.id}
                            className="lwd-card-enter"
                            data-listing-id={v.id}
                            style={{
                              ...getCardWrapperStyles(true),
                              outline: activeListingId === v.id ? "2px solid rgba(211,154,33,0.5)" : "none",
                              transition: "outline 0.2s",
                            }}
                            onMouseEnter={() => { setActiveListingId(v.id); PinSyncBus.emit("card:hover", v.id); }}
                            onMouseLeave={() => { setActiveListingId(null); PinSyncBus.emit("card:leave", v.id); }}
                          >
                            <LuxuryVenueCard
                              v={v}
                              onView={() => handleViewVenue(v)}
                              quickViewItem={qvItem}
                              setQuickViewItem={setQvItem}
                              matchedStyles={vendorFilters.style ? [vendorFilters.style] : []}
                              otherFilters={{ region: vendorFilters.region, capacity: vendorFilters.capacity }}
                              priority={idx === 0}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  /* Desktop: constrained grid inside maxWidth wrapper */
                  <div style={{ maxWidth: 1280, margin: "0 auto" }}>

                    {/* Zero result state, blocks grid */}
                    {listingCount === 0 && (
                      <EmptyResultState
                        resultCount={0}
                        categoryLabel={categoryLabel}
                        alternatives={alternativeCategories}
                        onSelectAlternative={handleSelectAlternative}
                        darkMode={darkMode}
                      />
                    )}

                    {/* wedding-venues · grid view, desktop: 1-col, 2-col ~1100px, 3-col large */}
                    {categorySlug === "wedding-venues" && viewMode === "grid" && listingCount > 0 && (
                      <div
                        className="lwd-venue-grid"
                        style={{
                          ...getGridStyles(false),
                          opacity: isFilteringTransition ? 0.7 : 1,
                          transition: "opacity 0.15s ease",
                        }}
                        aria-label="Venue grid"
                      >
                        {visibleVenueListings.map((v, idx) => (
                          <div
                            key={v.id}
                            className="lwd-card-enter"
                            data-listing-id={v.id}
                            style={{
                              ...getCardWrapperStyles(false),
                              outline: activeListingId === v.id ? "2px solid rgba(211,154,33,0.5)" : "none",
                              transition: "outline 0.2s",
                            }}
                            onMouseEnter={() => { setActiveListingId(v.id); PinSyncBus.emit("card:hover", v.id); }}
                            onMouseLeave={() => { setActiveListingId(null); PinSyncBus.emit("card:leave", v.id); }}
                          >
                            <LuxuryVenueCard
                              v={v}
                              onView={() => handleViewVenue(v)}
                              quickViewItem={qvItem}
                              setQuickViewItem={setQvItem}
                              matchedStyles={vendorFilters.style ? [vendorFilters.style] : []}
                              otherFilters={{ region: vendorFilters.region, capacity: vendorFilters.capacity }}
                              priority={idx === 0}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                  {/* wedding-venues · list view, single col (mobile only; desktop list always uses EXPLORE layout with map) */}
                  {categorySlug === "wedding-venues" && viewMode !== "grid" && isMobile && listingCount > 0 && (
                    <>
                      <div style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                        opacity: isFilteringTransition ? 0.7 : 1,
                        transition: "opacity 0.15s ease",
                      }}>
                        {visibleVenueListings.map((v) => (
                          <div
                            key={v.id}
                            data-listing-id={v.id}
                            onMouseEnter={() => { setActiveListingId(v.id); PinSyncBus.emit("card:hover", v.id); }}
                            onMouseLeave={() => { setActiveListingId(null); PinSyncBus.emit("card:leave", v.id); }}
                            onClick={() => PinSyncBus.emit("card:click", v.id)}
                          >
                            <VenueListItemCard
                              v={v}
                              onView={() => handleViewVenue(v)}
                              isHighlighted={activeListingId === v.id}
                              quickViewItem={qvItem}
                              setQuickViewItem={setQvItem}
                            />
                          </div>
                        ))}
                      </div>

                    </>
                  )}

                  {/* ── Vendor categories (config-driven card rendering) ── */}
                  {isVendor && !isVenue && viewMode === "grid" && listingCount > 0 && (
                    isMobile ? (
                      /* Mobile: responsive grid */
                      <div style={{
                        ...getGridStyles(true),
                        opacity: isFilteringTransition ? 0.7 : 1,
                        transition: "opacity 0.15s ease",
                      }}>
                        {visibleVenueListings.map((v) => (
                          <div key={v.id} className="lwd-card-enter" style={{ ...getCardWrapperStyles(true) }}>
                            {vendorFilterConfig?.card === "PlannerCard" ? (
                              <PlannerCard v={v} mode="grid" onView={() => onViewPlanner(v)} isMobile={isMobile} />
                            ) : (
                              <LuxuryVendorCard
                                v={v}
                                onView={() => handleViewVendor(v)}
                                onQuickView={() => track("card_quick_view", { id: v.id })}
                                isMobile={isMobile}
                                quickViewItem={qvItem}
                                setQuickViewItem={setQvItem}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* Desktop: grid */
                      <div style={{
                        ...getGridStyles(false),
                        opacity: isFilteringTransition ? 0.7 : 1,
                        transition: "opacity 0.15s ease",
                      }}>
                        {visibleVenueListings.map((v) => (
                          <div key={v.id} className="lwd-card-enter" style={{ ...getCardWrapperStyles(false) }}>
                            {vendorFilterConfig?.card === "PlannerCard" ? (
                              <PlannerCard v={v} mode="grid" onView={() => onViewPlanner(v)} isMobile={isMobile} />
                            ) : (
                              <LuxuryVendorCard
                                v={v}
                                onView={() => handleViewVendor(v)}
                                onQuickView={() => track("card_quick_view", { id: v.id })}
                                isMobile={isMobile}
                                quickViewItem={qvItem}
                                setQuickViewItem={setQvItem}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )
                  )}
                  {isVendor && !isVenue && viewMode !== "grid" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16, opacity: isFilteringTransition ? 0.7 : 1, transition: "opacity 0.15s ease" }}>
                      {visibleVenueListings.map((v) => (
                        vendorFilterConfig?.card === "PlannerCard" ? (
                          <PlannerCard
                            key={v.id}
                            v={v}
                            mode="list"
                            listMode
                            onView={() => onViewPlanner(v)}
                            isMobile={isMobile}
                            isHighlighted={activeListingId === v.id}
                          />
                        ) : (
                          <VenueListItemCard
                            key={v.id}
                            v={v}
                            onView={() => handleViewVendor(v)}
                            isHighlighted={activeListingId === v.id}
                            quickViewItem={qvItem}
                            setQuickViewItem={setQvItem}
                          />
                        )
                      ))}
                    </div>
                  )}

                  {hasMoreVenueCards && (
                    <LoadMoreVenuesButton C={C} onClick={() => setVisibleVenueCount(c => c + VENUE_CARDS_PAGE_SIZE)} />
                  )}

              </section>
            )}
          </>
        ) : listingsLoaded ? (
          /* ── Premium "Coming Soon" editorial state, only after fetch completes ── */
          <section
            className="lwd-rc-section"
            aria-label="Coming soon"
            style={{
              background: darkMode ? C.dark : "#f2f0ea",
              padding: isMobile ? "48px 20px 56px" : "96px 48px",
              minHeight: isMobile ? "auto" : undefined,
            }}
          >
            <div style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 28 }}>
                <div style={{ width: 28, height: 1, background: C.gold }} />
                <span
                  style={{
                    fontFamily: NU,
                    fontSize: 9,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    color: C.gold,
                    fontWeight: 600,
                  }}
                >
                  Coming Soon
                </span>
                <div style={{ width: 28, height: 1, background: C.gold }} />
              </div>
              <h2
                style={{
                  fontFamily: GD,
                  fontSize: "clamp(26px, 3vw, 36px)",
                  fontWeight: 400,
                  fontStyle: "italic",
                  color: C.off,
                  lineHeight: 1.2,
                  marginBottom: 20,
                }}
              >
                {categoryLabel}{regionName ? ` in ${regionName}` : ""}
              </h2>
              <p
                style={{
                  fontFamily: NU,
                  fontSize: 14,
                  color: C.grey,
                  lineHeight: 1.8,
                  fontWeight: 300,
                  marginBottom: 36,
                }}
              >
                Our editorial team is personally vetting {categoryLabel.toLowerCase()} in {regionName}.
                Premium listings are arriving soon, every recommendation is editorially
                verified, never pay-to-play.
              </p>
              <BrowseAllButton
                C={C}
                label={`Browse All ${categoryLabel}`}
                onClick={() => onViewCategory({ countrySlug, regionSlug })}
              />
            </div>
          </section>
        ) : null}


        {/* ════════════════════════════════════════════════════════════════════
            EDITORIAL GUIDE, below listings
        ════════════════════════════════════════════════════════════════════ */}
        {editorial && (
          <section
            className="lwd-rc-section"
            aria-label="Editorial introduction"
            style={{
              background: darkMode ? C.dark : "#f2f0ea",
              padding: isMobile ? "48px 20px" : "64px 32px",
              borderBottom: `1px solid ${darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)"}`,
            }}
          >
            <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
              <div style={{
                fontFamily: NU,
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: "2.5px",
                textTransform: "uppercase",
                color: C.gold,
                marginBottom: 24,
              }}>
                Editorial Guide
              </div>

              <p style={{
                fontFamily: GD,
                fontSize: isMobile ? 17 : 19,
                fontStyle: "italic",
                fontWeight: 400,
                color: darkMode ? "rgba(255,255,255,0.82)" : "#1a1a1a",
                lineHeight: 1.85,
                margin: 0,
              }}>
                {editorialExpanded
                  ? editorial
                  : editorial.split(". ").slice(0, 2).join(". ") + "."}
              </p>

              {editorial.split(". ").length > 2 && (
                <button
                  onClick={() => setEditorialExpanded(e => !e)}
                  style={{
                    marginTop: 20,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: NU,
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    color: C.gold,
                    padding: 0,
                    opacity: 0.75,
                    transition: "opacity 0.2s",
                  }}
                  onMouseEnter={e => e.target.style.opacity = "1"}
                  onMouseLeave={e => e.target.style.opacity = "0.75"}
                >
                  {editorialExpanded ? "Read less" : "Read more"}
                </button>
              )}
            </div>
          </section>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            7. WEDDING VENDORS, carousel (standard section)
        ════════════════════════════════════════════════════════════════════ */}
        {useMemo(() => {
          const activeCatSet = new Set();
          dbListings.forEach(l => {
            const cat = l.categorySlug || l.category_slug || "";
            if (cat) activeCatSet.add(cat);
            const lt = l.listingType || l.listing_type || "";
            if (!cat && (lt === "venue" || !lt)) activeCatSet.add("wedding-venues");
          });
          if (finalListings.length > 0) activeCatSet.add(categorySlug);
          return (
            <section
              className="lwd-rc-section"
              aria-label="Wedding vendors"
              style={{
                padding: isMobile ? "40px 16px" : "96px 120px",
              }}
            >
              <div style={{ maxWidth: 1280, margin: "0 auto" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 16 }}>
                  <div style={{ width: 28, height: 1, background: C.gold }} />
                  <span style={{ fontFamily: NU, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: C.gold, fontWeight: 600 }}>
                    Find Your Team
                  </span>
                  <div style={{ width: 28, height: 1, background: C.gold }} />
                </div>
                <h2 style={{ fontFamily: GD, fontSize: isMobile ? "clamp(22px, 6vw, 30px)" : "clamp(26px, 3vw, 36px)", fontWeight: 400, color: C.off, lineHeight: 1.2, margin: "0 0 32px", textAlign: "center", width: "100%" }}>
                  {regionName ? <><span style={{ color: C.grey }}>{regionName}</span>{" "}</> : null}
                  <span style={{ fontStyle: "italic", color: C.white }}>Wedding Vendors</span>
                </h2>
                <VendorCategoryCarousel
                  categories={VENDOR_CATEGORIES}
                  C={C}
                  onSelect={(slug) => onViewRegionCategory(countrySlug, regionSlug, slug)}
                  activeCategorySlugs={activeCatSet}
                  isMobile={isMobile}
                />
              </div>
            </section>
          );
        }, [isVenue, categorySlug, regionName, isMobile, dbListings, finalListings])}


        {/* Related Regions, removed per user request */}


        {/* ════════════════════════════════════════════════════════════════════
            9. SEO PANEL, collapsible (dev only)
        ════════════════════════════════════════════════════════════════════ */}
        {false && (
        <section
          className="lwd-rc-section"
          aria-label="SEO data"
          style={{
            background: C.black,
            padding: "48px 48px",
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <div style={{ maxWidth: 800, margin: "0 auto" }}>
            <button
              onClick={() => setSeoExpanded((e) => !e)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 12,
                width: "100%",
                padding: 0,
              }}
            >
              <div style={{ width: 28, height: 1, background: C.gold }} />
              <span
                style={{
                  fontFamily: NU,
                  fontSize: 9,
                  letterSpacing: "0.3em",
                  textTransform: "uppercase",
                  color: C.gold,
                  fontWeight: 600,
                }}
              >
                SEO & Canonical Data
              </span>
              <div style={{ width: 28, height: 1, background: C.gold }} />
              <span
                style={{
                  fontFamily: NU,
                  fontSize: 14,
                  color: C.grey,
                  marginLeft: "auto",
                  transition: "transform 0.2s",
                  transform: seoExpanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                ▾
              </span>
            </button>

            {seoExpanded && (
              <div style={{ marginTop: 32 }}>
                <div style={{ marginBottom: 24 }}>
                  <h3
                    style={{
                      fontFamily: NU,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: C.off,
                      marginBottom: 12,
                    }}
                  >
                    Canonical Path
                  </h3>
                  <code
                    style={{
                      fontSize: 13,
                      color: C.gold,
                      fontFamily: "monospace",
                      background: C.card,
                      padding: "8px 16px",
                      borderRadius: "var(--lwd-radius-input)",
                      display: "inline-block",
                    }}
                  >
                    {canonicalPath}
                  </code>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <h3
                    style={{
                      fontFamily: NU,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: C.off,
                      marginBottom: 12,
                    }}
                  >
                    Page Title
                  </h3>
                  <div style={{ fontSize: 13, color: C.grey, fontFamily: NU, lineHeight: 1.7 }}>
                    {categoryLabel}{regionName ? ` in ${regionName}` : ""}, Luxury Wedding Directory
                  </div>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <h3
                    style={{
                      fontFamily: NU,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: C.off,
                      marginBottom: 12,
                    }}
                  >
                    Meta Description
                  </h3>
                  <div style={{ fontSize: 13, color: C.grey, fontFamily: NU, lineHeight: 1.7 }}>
                    Discover {listingCount > 0 ? listingCount : "the finest"} curated {categoryLabel.toLowerCase()}{regionName ? ` in ${regionName}` : ""}. Every recommendation editorially verified by the LWD team.
                  </div>
                </div>

                <div>
                  <h3
                    style={{
                      fontFamily: NU,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: C.off,
                      marginBottom: 12,
                    }}
                  >
                    Entity Data
                  </h3>
                  <div style={{ fontSize: 12, color: C.grey, fontFamily: "monospace", lineHeight: 1.8 }}>
                    <div>country: <span style={{ color: C.gold }}>{countrySlug}</span></div>
                    <div>region: <span style={{ color: C.gold }}>{regionSlug}</span></div>
                    <div>category: <span style={{ color: C.gold }}>{categorySlug}</span></div>
                    <div>listings: <span style={{ color: C.gold }}>{listingCount}</span></div>
                    <div>relatedRegions: <span style={{ color: C.gold }}>{relatedRegions.length}</span></div>
                    <div>siblingCategories: <span style={{ color: C.gold }}>{siblingCategories.length}</span></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
        )}


        {/* ════════════════════════════════════════════════════════════════════
            10. BROWSE BY REGION
        ════════════════════════════════════════════════════════════════════ */}
        <DirectoryBrands onViewRegion={onViewRegion} onViewCategory={onViewCategory} showInternational={false} showUK={countrySlug === "england"} showItaly={countrySlug === "italy"} showUSA={countrySlug === "usa"} liveRegions={regionSlug && countrySlug ? [{ slug: regionSlug, name: regionName, countrySlug }] : []} darkMode={darkMode} />


        {/* ══════════════════════════════════════════════════════════════════
            MOBILE MAP, floating pill + bottom sheet
            Visible on mobile whenever there are listings, any view mode
        ═══════════════════════════════════════════════════════════════════ */}
        {isMobile && listingCount > 0 && (
          <>
            {/* Floating pill, fixed bottom-centre, above Aura/compare bars */}
            {!mapOpen && (
              <div style={{
                position: "fixed",
                bottom: 28,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 900,
              }}>
                <button
                  className="lwd-map-pill"
                  onClick={() => setMapOpen(true)}
                  aria-label="Show map"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                    <circle cx="12" cy="9" r="2.5" fill="currentColor" stroke="none"/>
                  </svg>
                  Map
                  <span className="lwd-map-pill-count">{listingCount}</span>
                </button>
              </div>
            )}

            {/* Bottom sheet */}
            {mapOpen && (
              <div
                className="lwd-map-sheet-backdrop"
                onClick={() => setMapOpen(false)}
              >
                <div
                  className="lwd-map-sheet"
                  style={{ background: darkMode ? C.dark : "#f2f0ea" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Drag handle */}
                  <div className="lwd-map-sheet-handle">
                    <div className="lwd-map-sheet-handle-bar" />
                  </div>

                  {/* Header */}
                  <div
                    className="lwd-map-sheet-header"
                    style={{ borderBottom: `1px solid ${C.border}` }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{
                        fontFamily: NU, fontSize: 11, fontWeight: 700,
                        letterSpacing: "1.5px", textTransform: "uppercase", color: C.gold,
                      }}>
                        {categoryLabel}
                      </span>
                      <span style={{ fontFamily: NU, fontSize: 11, color: C.grey }}>
                        {listingCount} {listingCount === 1 ? "listing" : "listings"}{regionName ? ` · ${regionName}` : ""}
                      </span>
                    </div>
                    <button
                      className="lwd-map-sheet-close"
                      style={{ color: C.grey }}
                      onClick={() => setMapOpen(false)}
                      aria-label="Close map"
                    >
                      ×
                    </button>
                  </div>

                  {/* Map, fills remaining height */}
                  <div style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
                    <MASTERMap
                      items={finalListings}
                      label={`${categoryLabel} · ${regionName || countryName || "Italy"}`}
                      viewMode={viewMode}
                      onToggleView={() => setMapOpen(false)}
                      countrySlug={countrySlug || "italy"}
                      // Match the destination pages exactly. LocationPage, CountryTemplate and
                    // USAPage all pass C.black, which is theme-aware (#000000 dark, #fbf7f4
                    // light); this passed a hardcoded #f2f0ea in light mode, so the map's
                    // left-edge gradient washed cream here and black on the destination pages
                    // even though both render the same CARTO dark_all tiles.
                    pageBg={C.black}
                      auraSummary={auraSummary && !summaryDismissed ? auraSummary : null}
                      activeFilter={auraMapFilter}
                      onFilterChange={(cat) => setAuraMapFilter(cat)}
                      auraRecommendedIds={auraRecommendedIds}
                      onSparsePins={handleSparsePins}
                      activeListingId={activeListingId}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Quick View modal (page-level) ── */}
        {qvItem && (
          <QuickViewModal
            item={qvItem}
            onClose={() => setQvItem(null)}
            onViewFull={(v) => { setQvItem(null); onViewVenue(v); }}
          />
        )}

        <ImmersiveSearch
          isOpen={immersiveOpen}
          onClose={() => setImmersiveOpen(false)}
          onViewCategory={onViewCategory}
          onViewRegionCategory={onViewRegionCategory}
        />
      </div>
      </ThemeCtx.Provider>
    </>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS, local to this file
// ═════════════════════════════════════════════════════════════════════════════


// ── Nav ────────────────────────────────────────────────────────────────────
function RegionCategoryNav({ onBack, onBackHome, scrolled, darkMode, onToggleDark, C, countryName, regionName, categoryLabel, countrySlug, regionSlug }) {
  const [hovBack, setHovBack] = useState(false);
  const [hovHome, setHovHome] = useState(false);
  const [hovRegion, setHovRegion] = useState(false);
  const [hovToggle, setHovToggle] = useState(false);

  const ghostBorder = scrolled ? C.border2 : "rgba(255,255,255,0.25)";
  const ghostColor = scrolled ? C.grey : "rgba(255,255,255,0.7)";

  return (
    <nav
      className="lwd-rc-nav"
      aria-label="Page navigation"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 900,
        padding: scrolled ? "12px 48px" : "18px 48px",
        background: scrolled
          ? darkMode ? "rgba(8,8,8,0.97)" : "rgba(250,248,245,0.97)"
          : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled ? `1px solid ${C.border}` : "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        transition: "all 0.35s ease",
      }}
    >
      {/* Left: back + breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
        <button
          onClick={onBack}
          aria-label="Go back to region"
          onMouseEnter={() => setHovBack(true)}
          onMouseLeave={() => setHovBack(false)}
          style={{
            background: "none",
            border: `1px solid ${hovBack ? C.gold : ghostBorder}`,
            borderRadius: "var(--lwd-radius-input)",
            color: hovBack ? C.gold : ghostColor,
            padding: "7px 14px",
            fontSize: 11,
            letterSpacing: "1px",
            textTransform: "uppercase",
            cursor: "pointer",
            fontFamily: "inherit",
            transition: "all 0.2s",
          }}
        >
          ← Back
        </button>

      </div>

      {/* Centre: logo */}
      <button
        className="lwd-rc-logo"
        onClick={onBackHome}
        aria-label="Luxury Wedding Directory home"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          fontFamily: GD,
          fontSize: 18,
          fontWeight: 600,
          color: scrolled ? C.white : "#ffffff",
          letterSpacing: 0.5,
        }}
      >
        Luxury{" "}
        <span style={{ color: C.gold }}>Wedding</span>{" "}
        Directory
      </button>

      {/* Right: theme toggle */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          onClick={onToggleDark}
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          title="Toggle theme"
          onMouseEnter={() => setHovToggle(true)}
          onMouseLeave={() => setHovToggle(false)}
          style={{
            background: "none",
            border: `1px solid ${hovToggle ? C.gold : C.border2}`,
            borderRadius: "var(--lwd-radius-input)",
            color: hovToggle ? C.gold : C.grey,
            width: 34,
            height: 34,
            cursor: "pointer",
            fontSize: 14,
            transition: "all 0.2s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {darkMode ? "☀" : "☽"}
        </button>
      </div>
    </nav>
  );
}


// ── Listing Card, works for both venues and vendors ─────────────────────
function ListingCard({ item, C, isVenue, onView }) {
  const [hov, setHov] = useState(false);

  return (
    <article
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: C.card,
        border: `1px solid ${hov ? C.gold : C.border2}`,
        borderRadius: "var(--lwd-radius-card)",
        overflow: "hidden",
        cursor: isVenue ? "pointer" : "default",
        transition: "all 0.25s",
      }}
      onClick={() => isVenue && onView?.(item)}
    >
      <div style={{ position: "relative", height: 200, overflow: "hidden" }}>
        <img
          src={imgUrl(item.imgs?.[0], { width: 800 })}
          alt={item.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: hov ? "scale(1.05)" : "scale(1)",
            transition: "transform 0.5s ease",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg,transparent 50%,rgba(0,0,0,0.6) 100%)",
          }}
        />
        {item.tag && (
          <span
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              background: "rgba(211,154,33,0.9)",
              color: "#0f0d0a",
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              padding: "4px 10px",
              borderRadius: "var(--lwd-radius-input)",
              fontFamily: NU,
            }}
          >
            {item.tag}
          </span>
        )}
        {item.verified && (
          <span
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              background: "rgba(255,255,255,0.12)",
              color: "#d39a21",
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "1px",
              textTransform: "uppercase",
              padding: "4px 10px",
              borderRadius: "var(--lwd-radius-input)",
              fontFamily: NU,
              backdropFilter: "blur(4px)",
            }}
          >
            ✓ Verified
          </span>
        )}
      </div>
      <div style={{ padding: "20px 20px 24px" }}>
        <h3
          style={{
            fontFamily: GD,
            fontSize: 20,
            fontWeight: 400,
            color: C.off,
            marginBottom: 6,
            fontStyle: "italic",
          }}
        >
          {item.name}
        </h3>
        <div
          style={{
            fontSize: 12,
            color: C.grey,
            fontFamily: NU,
            marginBottom: 8,
          }}
        >
          {item.city}{item.city && item.region ? ", " : ""}{item.region}
        </div>
        {item.desc && (
          <p
            style={{
              fontFamily: NU,
              fontSize: 13,
              color: C.grey,
              lineHeight: 1.65,
              fontWeight: 300,
              marginBottom: 12,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {item.desc}
          </p>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 13, color: C.gold, fontWeight: 700, fontFamily: NU }}>
            {item.rating} ★
          </span>
          {item.reviews > 0 && (
            <span style={{ fontSize: 11, color: C.grey, fontFamily: NU }}>
              ({item.reviews} reviews)
            </span>
          )}
          <span style={{ fontSize: 11, color: C.grey, fontFamily: NU, marginLeft: "auto" }}>
            From {item.priceFrom}
          </span>
        </div>
      </div>
    </article>
  );
}


// ── Luxury SVG icons for category cards ──────────────────────────────────────
const LUXURY_ICONS = {
  "wedding-venues": (color) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18" />
      <path d="M5 21V7l7-4 7 4v14" />
      <path d="M9 21v-6h6v6" />
      <path d="M10 10h.01M14 10h.01" />
    </svg>
  ),
  "wedding-planners": (color) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  ),
  "photographers": (color) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  ),
  "florists": (color) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22c0 0 0-3 0-6" />
      <path d="M9 18c-2 0-4-1.5-4-4 0-2 2-3.5 4-3.5.5-2 2-3.5 3-3.5s2.5 1.5 3 3.5c2 0 4 1.5 4 3.5 0 2.5-2 4-4 4" />
      <path d="M12 8c0-2 1-4 3-5" />
      <path d="M12 8c0-2-1-4-3-5" />
    </svg>
  ),
  "caterers": (color) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8h1a4 4 0 010 8h-1" />
      <path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z" />
      <line x1="6" y1="1" x2="6" y2="4" />
      <line x1="10" y1="1" x2="10" y2="4" />
      <line x1="14" y1="1" x2="14" y2="4" />
    </svg>
  ),
  "hair-makeup": (color) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="5" />
      <path d="M12 13v8" />
      <path d="M9 18h6" />
      <path d="M15 5c1-2 3-3 4-2" />
    </svg>
  ),
  "entertainment": (color) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  ),
  "videographers": (color) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  ),
  "wedding-cakes": (color) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 18h16v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2z" />
      <path d="M6 14h12v4H6z" />
      <path d="M8 10h8v4H8z" />
      <path d="M12 3v3" />
      <circle cx="12" cy="2" r="1" />
    </svg>
  ),
  "stationery": (color) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  "bridal-wear": (color) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2C9 2 7 5 7 8c0 2 1 3 2 4l-3 10h12l-3-10c1-1 2-2 2-4 0-3-2-6-5-6z" />
      <path d="M9 22h6" />
    </svg>
  ),
  "jewellers": (color) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="14" r="6" />
      <path d="M12 8V2" />
      <path d="M8 10l-3-5" />
      <path d="M16 10l3-5" />
    </svg>
  ),
  "transport": (color) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 17h14v-5H5v5z" />
      <path d="M2 12h20" />
      <path d="M5 12V7c0-1.7 1.3-3 3-3h8c1.7 0 3 1.3 3 3v5" />
      <circle cx="7" cy="17" r="2" />
      <circle cx="17" cy="17" r="2" />
    </svg>
  ),
  "event-design": (color) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
};

// ── Sibling Category Card ──────────────────────────────────────────────────
function SiblingCategoryCard({ vc, C, onClick }) {
  const [hov, setHov] = useState(false);
  const iconColor = hov ? C.gold : (C.grey || "#888");
  const renderIcon = LUXURY_ICONS[vc.slug];

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? C.card : C.dark,
        border: `1px solid ${hov ? C.gold : C.border2}`,
        borderRadius: "var(--lwd-radius-card)",
        padding: "28px 20px",
        textAlign: "center",
        cursor: "pointer",
        transition: "all 0.3s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
        flexShrink: 0,
        minWidth: "calc((100% - 55px) / 5.5)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: hov ? C.goldDim : "transparent",
          border: `1px solid ${hov ? C.gold : C.border2}`,
          transition: "all 0.3s ease",
        }}
      >
        {renderIcon ? renderIcon(iconColor) : (
          <span style={{ fontSize: 22, opacity: 0.6 }}>{vc.icon}</span>
        )}
      </span>
      <span
        style={{
          fontFamily: NU,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "1px",
          textTransform: "uppercase",
          color: hov ? C.gold : C.off,
          transition: "color 0.2s",
        }}
      >
        {vc.label}
      </span>
    </button>
  );
}


// ── Vendor Category Carousel (matches RegionPage) ───────────────────────
const VENDOR_CATS_PER_PAGE = 6;

function VendorCategoryCarousel({ categories, C, onSelect, activeCategorySlugs = null, isMobile = false }) {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(categories.length / VENDOR_CATS_PER_PAGE);
  const start = page * VENDOR_CATS_PER_PAGE;
  const visible = categories.slice(start, start + VENDOR_CATS_PER_PAGE);

  // H5 fix: reset to page 0 when the category list changes (e.g. on category switch)
  useEffect(() => { setPage(0); }, [categories]);

  const [hovPrev, setHovPrev] = useState(false);
  const [hovNext, setHovNext] = useState(false);

  const arrowBtn = (dir, hov, setHov, disabled, onClick) => (
    <button
      aria-label={dir === "prev" ? "Previous categories" : "Next categories"}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov && !disabled ? (C.goldDim || "rgba(211,154,33,0.08)") : "transparent",
        border: `1px solid ${disabled ? (C.border || "rgba(255,255,255,0.06)") : hov ? C.gold : (C.border2 || "rgba(255,255,255,0.12)")}`,
        borderRadius: "50%",
        width: 36,
        height: 36,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.25 : 1,
        transition: "all 0.25s",
        flexShrink: 0,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={hov && !disabled ? C.gold : (C.grey || "#888")} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {dir === "prev" ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 6 15 12 9 18" />}
      </svg>
    </button>
  );

  // Mobile: horizontal scroll rail, show all cards, no pagination.
  // Never force the locked 200px card into a grid cell, it will clip.
  if (isMobile) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          overflowX: "auto",
          gap: 10,
          paddingBottom: 4,
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {categories.map((vc) => (
          <div key={vc.slug} style={{ flexShrink: 0, scrollSnapAlign: "start" }}>
            <VendorCategoryCard
              vc={vc}
              C={C}
              onClick={() => onSelect(vc.slug)}
              isEmpty={activeCategorySlugs !== null && !activeCategorySlugs.has(vc.slug)}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr)",
          gap: 16,
        }}
      >
        {visible.map((vc) => (
          <VendorCategoryCard
            key={vc.slug}
            vc={vc}
            C={C}
            onClick={() => onSelect(vc.slug)}
            isEmpty={activeCategorySlugs !== null && !activeCategorySlugs.has(vc.slug)}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            marginTop: 28,
          }}
        >
          {arrowBtn("prev", hovPrev, setHovPrev, page === 0, () => setPage((p) => p - 1))}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                aria-label={`Page ${i + 1}`}
                onClick={() => setPage(i)}
                style={{
                  width: page === i ? 20 : 6,
                  height: 6,
                  borderRadius: 3,
                  background: page === i ? C.gold : (C.border2 || "rgba(255,255,255,0.12)"),
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </div>
          {arrowBtn("next", hovNext, setHovNext, page >= totalPages - 1, () => setPage((p) => p + 1))}
        </div>
      )}
    </div>
  );
}

function VendorCategoryCard({ vc, C, onClick, isEmpty = false }) {
  return (
    <MasterCategoryCard
      category={vc}
      colors={C}
      onClick={onClick}
      isEmpty={isEmpty}
    />
  );
}


// ── Related Region Card ──────────────────────────────────────────────────
function RelatedRegionCard({ region, categoryLabel, C, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? C.card : "transparent",
        border: `1px solid ${hov ? C.gold : C.border2}`,
        borderRadius: "var(--lwd-radius-card)",
        padding: "28px 24px",
        textAlign: "left",
        cursor: "pointer",
        transition: "all 0.25s",
        display: "block",
        flexShrink: 0,
        minWidth: 260,
      }}
    >
      <div
        style={{
          fontFamily: GD,
          fontSize: 20,
          fontWeight: 400,
          fontStyle: "italic",
          color: hov ? C.gold : C.off,
          marginBottom: 6,
          transition: "color 0.2s",
        }}
      >
        {region.name}
      </div>
      <div
        style={{
          fontFamily: NU,
          fontSize: 11,
          color: C.grey,
          letterSpacing: "0.5px",
        }}
      >
        {categoryLabel} · {region.group || ""}
      </div>
    </button>
  );
}


// ── Search Button ──────────────────────────────────────────────────────────
function SearchButton({ C }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      type="submit"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? "linear-gradient(135deg,#d39a21,#9b7a1a)" : `linear-gradient(135deg,${C.gold},${C.gold2 || "#e8c97a"})`,
        border: "none",
        borderRadius: "var(--lwd-radius-input)",
        color: "#0f0d0a",
        padding: "14px 28px",
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "2px",
        textTransform: "uppercase",
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "all 0.2s",
        whiteSpace: "nowrap",
      }}
    >
      Search →
    </button>
  );
}


// ── Browse All Button ─────────────────────────────────────────────────────
function BrowseAllButton({ C, label, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? C.gold : "none",
        border: `1px solid ${C.gold}`,
        borderRadius: "var(--lwd-radius-input)",
        color: hov ? "#0f0d0a" : C.gold,
        padding: "13px 36px",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "2px",
        textTransform: "uppercase",
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "all 0.25s",
      }}
    >
      {label} →
    </button>
  );
}

// Reveals the next batch of already-fetched, already-filtered cards, no
// refetch, just raises the render-slice count. See visibleVenueCount.
function LoadMoreVenuesButton({ onClick, C }) {
  const [hov, setHov] = useState(false);
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "36px 0 8px" }}>
      <button
        onClick={onClick}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          background: hov ? C.gold : "none",
          border: `1px solid ${C.gold}`,
          borderRadius: "var(--lwd-radius-input)",
          color: hov ? "#0f0d0a" : C.gold,
          padding: "13px 36px",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "2px",
          textTransform: "uppercase",
          cursor: "pointer",
          fontFamily: "inherit",
          transition: "all 0.25s",
        }}
      >
        Load More
      </button>
    </div>
  );
}
